import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, PLACEHOLDER, FieldRow, KeepTogether, Section, SectionNumberingProvider, SubClause, SubClauseProvider, resolveSectionPath, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, attachmentLabel, attachmentLabels, hasAttachmentRef, AttachmentList, } from '../../../atoms/index.js';
import { expandPartyProjection, COTENANT_SUFFIX } from '../../../parties/index.js';
import { addMonths, parseDateValue, parseNumberValue, postSigningRule, subtractMonths, } from '../../../notification-rules.js';
/**
 * Notification rules (R5). Lokalhyra is always fixed-term and
 * automatically extends under §4 if no party gives notice. The
 * lifecycle is therefore a series of rolling renewal points; we
 * remind on each one until the contract is acknowledged or
 * cancelled.
 *
 * Rules:
 *   - termination_notice — recurring. X = notice_months + 1 months
 *     before each renewal point. The 0-th renewal point is the
 *     initial lease_end_date; subsequent points are at
 *     lease_end_date + i × extension_months. We materialize 5 cycles
 *     by default, covering ~15 years of 3-year auto-extensions.
 *   - lease_end — one-shot at the initial lease_end_date (the
 *     calendar event itself; useful as a "first auto-renewal
 *     happens now" reminder).
 */
const LOKALHYRA_RECURRING_MAX_CYCLES = 5;
const lokalhyraNotificationRules = [
    {
        ruleKey: 'termination_notice',
        kind: 'termination_notice',
        label: 'Uppsägning',
        phase: 'post_signing',
        resolve: (ctx) => {
            const leaseEnd = parseDateValue(ctx.values.lease_end_date);
            const notice = parseNumberValue(ctx.values.notice_months);
            const extension = parseNumberValue(ctx.values.extension_months);
            if (!leaseEnd || notice === null || notice <= 0) {
                return [];
            }
            const X = notice + 1;
            // The notice deadline before the INITIAL lease end always applies
            // when there's a lease end + a notice period — that's the date the
            // tenant/landlord must act by. Auto-extension (förlängningstid) only
            // adds the recurring renewal points after it. So when the document
            // doesn't state an extension (common on imports — the LLM leaves it
            // null rather than guess), still emit that first reminder; only the
            // recurring cycles are skipped. Previously a missing extension
            // suppressed the uppsägning reminder entirely.
            const hasExtension = extension !== null && extension > 0;
            const cycles = hasExtension ? LOKALHYRA_RECURRING_MAX_CYCLES : 1;
            const Y = hasExtension ? extension : 0;
            const occurrences = [];
            for (let i = 1; i <= cycles; i++) {
                const cycleEnd = addMonths(leaseEnd, (i - 1) * Y);
                const fireAt = subtractMonths(cycleEnd, X);
                occurrences.push({
                    triggerAt: cycleEnd,
                    fireAt,
                    leadLabel: 'custom',
                    label: i === 1
                        ? `${X} månader före hyresavtalet löper ut`
                        : `${X} månader före automatisk förlängning nr ${i - 1} löper ut`,
                });
            }
            return occurrences;
        },
    },
    postSigningRule({
        ruleKey: 'lease_end',
        kind: 'lease_end',
        label: 'Hyresavtalet löper ut',
        resolveTriggerAt: (values) => parseDateValue(values.lease_end_date),
    }),
];
export const metadata = {
    templateKey: 'lokalhyra',
    version: 'v1',
    category: 'Lokalhyra',
    title: 'Lokalhyresavtal · Version 2026',
    shortTitle: 'Lokalhyresavtal',
    titleSuffix: { key: 'object_number', prefix: 'objekt' },
    formNumber: 'Formulär R-02',
    preamble: 'Mellan hyresvärd och hyresgäst har följande avtal ingåtts.',
    status: 'active',
    partySlots: [
        {
            id: 'lessor',
            label: 'Hyresvärd',
            role: 'lessor',
            ordinal: 0,
            required: true,
        },
        {
            id: 'lessee',
            label: 'Hyresgäst',
            role: 'lessee',
            ordinal: 0,
            required: true,
        },
        {
            id: 'lessee_co',
            label: 'Medhyresgäst',
            role: 'lessee',
            ordinal: 1,
            help: 'Valfritt — fyll i om avtalet har en andra hyresgäst.',
        },
        {
            id: 'guarantor',
            label: 'Borgensman',
            role: 'guarantor',
            ordinal: 0,
            help: 'Valfritt — fylls i om säkerhetstypen är "Proprieborgen".',
            visibleWhen: { security_type: 'Proprieborgen' },
        },
    ],
    // Party projection (ADR-0065) for the lessor/lessee/medhyresgäst slots —
    // split address. The borgensman keeps its hand-declared "Borgensman – …"
    // fields (section "sakerhet"); the document body is not PartyBlock-driven.
    partyProjection: [
        {
            slot: 'lessor',
            section: 'hyresvard',
            idHint: 'org',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'landlord_name',
                    requiredAtFinal: true,
                    help: 'Fullständigt namn eller företagsnamn',
                },
                { attr: 'id_number', key: 'landlord_org_number', requiredAtFinal: true, help: 'XXXXXX-XXXX' },
                { attr: 'street', key: 'landlord_address', requiredAtFinal: true },
                {
                    attr: 'postal_line',
                    key: 'landlord_postal_address',
                    requiredAtFinal: true,
                    help: '123 45 Ort',
                },
            ],
        },
        {
            slot: 'lessee',
            section: 'hyresgast',
            idHint: 'org',
            fields: [
                {
                    attr: 'legal_name',
                    key: 'tenant_name',
                    requiredAtFinal: true,
                    help: 'Fullständigt namn eller företagsnamn',
                },
                { attr: 'id_number', key: 'tenant_org_number', requiredAtFinal: true, help: 'XXXXXX-XXXX' },
                { attr: 'street', key: 'tenant_address', requiredAtFinal: true },
                {
                    attr: 'postal_line',
                    key: 'tenant_postal_address',
                    requiredAtFinal: true,
                    help: '123 45 Ort',
                },
            ],
        },
        {
            slot: 'lessee_co',
            section: 'hyresgast',
            idHint: 'org',
            labelSuffix: COTENANT_SUFFIX,
            fields: [
                {
                    attr: 'legal_name',
                    key: 'cotenant_name',
                    help: 'Valfritt — fullständigt namn eller företagsnamn',
                },
                { attr: 'id_number', key: 'cotenant_org_number', help: 'XXXXXX-XXXX' },
                { attr: 'street', key: 'cotenant_address' },
                {
                    attr: 'postal_line',
                    key: 'cotenant_postal_address',
                    help: '123 45 Ort',
                },
            ],
        },
    ],
    /**
     * Per ADR-0028 addendum 2026-05-27. Only Bilaga 1 (planlösning) is a
     * reserved fixed slot, and it's opt-out-able via `include_planlosning`
     * — unchecked, the slot disappears and freeform bilagor compact from
     * Bilaga 1. The skick / lås / underhåll bilagor are no longer
     * fixed-number slots — their clauses reference a picked bilaga
     * dynamically (Pattern B: `condition_annex` / `lock_annex` /
     * `maintenance_premises_annex` / `maintenance_common_annex` /
     * `maintenance_ancillary_annex`), so the legal text carries no
     * hardcoded number.
     */
    attachmentSlots: [
        {
            id: 'bilaga_1_planlosning',
            label: 'Lokalens disposition och planlösning',
            fixedNumber: 1,
            required: true,
            visibleWhen: { include_planlosning: true },
            help: 'Ritning eller skiss över lokalens disposition.',
        },
    ],
    // Document sections in fixed order (per ADR-0049). §1–11 carry editor
    // fields; §12–26 are field-less static legal clauses (+ Bilagor) that
    // are declared here only so they get numbered. None are conditional —
    // lokalhyra always renders all 26 — so numbering is stable 1–26; the
    // model still buys editor↔document alignment, panel § labels, and
    // scroll-to-section on this 26-section template.
    sections: [
        { ref: 'hyresvard', title: 'Hyresvärd' },
        { ref: 'hyresgast', title: 'Hyresgäst' },
        { ref: 'hyresobjekt', title: 'Hyresobjekt' },
        { ref: 'hyrestid-uppsagning', title: 'Hyrestid och uppsägning' },
        { ref: 'anvandning', title: 'Lokalens användning' },
        { ref: 'skick', title: 'Lokalens skick' },
        { ref: 'hyra', title: 'Hyra' },
        { ref: 'arlig-hojning', title: 'Hyrans årsvisa höjning' },
        {
            // Per "Taxebundna tillägg.docx" — replaces the legacy per-mediatyp
            // selectors with two sibling alternatives covering el (9.1) and
            // värme/kyla/drift/skötsel (9.2). Sub-refs mirror the Body's
            // SubClause atoms so the editor labels match the rendered numbers.
            ref: 'forbrukning',
            title: 'Taxebundna tillägg: El, värme, kyla och drift',
            subClauses: [
                { ref: 'el', title: 'Ersättning för el' },
                { ref: 'drift', title: 'Ersättning för värme, kyla, drift och skötsel m.m.' },
            ],
        },
        { ref: 'avfallshantering', title: 'Avfallshantering' },
        { ref: 'bevakning', title: 'Bevakning' },
        { ref: 'sakerhet', title: 'Säkerhet' },
        { ref: 'besittningsskydd', title: 'Besittningsskydd' },
        { ref: 'las-stold', title: 'Lokalens lås- och stöldskyddsanordningar' },
        {
            ref: 'underhall',
            title: 'Underhållsansvar',
            subClauses: [
                { ref: 'lokalen', title: 'Avseende lokalen' },
                { ref: 'gemensamma', title: 'Avseende allmänna och gemensamma utrymmen' },
                {
                    ref: 'biutrymmen',
                    title: 'Avseende tillhörande biutrymmen',
                    visibleWhen: { ancillary_spaces_maintenance: true },
                },
                {
                    ref: 'pafoljd',
                    title: 'Påföljd vid hyresgästs åsidosättande av underhållsskyldighet',
                },
            ],
        },
        {
            ref: 'ombyggnad',
            title: 'Ombyggnads- och ändringsarbeten',
            subClauses: [
                { ref: 'ratt', title: 'Hyresgästens rätt att utföra ombyggnads- eller ändringsarbeten' },
                { ref: 'insyn', title: 'Hyresvärdens insyn under byggtid' },
                {
                    ref: 'byggvarudeklaration',
                    title: 'Byggvarudeklaration',
                    visibleWhen: { byggvarudeklaration_included: true },
                },
                { ref: 'avgifter', title: 'Offentligrättsliga avgifter vid olovliga ändringar' },
                {
                    ref: 'nedsattning',
                    title: 'Nedsättning av hyra vid sedvanligt underhåll',
                    subClauses: [
                        { ref: 'avtalat-skick', title: 'Avtalat skick m.m.' },
                        { ref: 'sedvanligt-underhall', title: 'Sedvanligt underhåll' },
                    ],
                },
            ],
        },
        { ref: 'skyltning', title: 'Skyltning' },
        { ref: 'ledningar', title: 'Ledningar för telefoni m.m.' },
        { ref: 'myndighetsforeskrifter', title: 'Myndighetsföreskrifter' },
        {
            ref: 'brandskydd',
            title: 'Brandskydd',
            subClauses: [
                { ref: 'tilltrade', title: 'Ansvar vid tillträdet' },
                { ref: 'underhall-utbyte', title: 'Underhåll och utbyte' },
                { ref: 'sprinkler', title: 'Sprinklerarbeten' },
                { ref: 'systematiskt', title: 'Systematiskt brandskyddsarbete' },
            ],
        },
        { ref: 'utrymningsvagar', title: 'Utrymningsvägar' },
        {
            ref: 'skatter-avgifter',
            title: 'Skatter och avgifter',
            // Sub-clauses mirror the Body's SubClauseProvider chain — editor
            // resolves each field's dotted `section` path against this tree
            // to compose the live label prefix (e.g. "22.3.a"). Visibilities
            // follow the same rules used in the Body.
            subClauses: [
                { ref: 'fastighetsskatt', title: 'Fastighetsskatt' },
                {
                    ref: 'oforutsedda',
                    title: 'Oförutsedda kostnader – andelstal',
                    // 17.2 is omitted when no category is marked. visibleWhen is
                    // expressed as "at least one of these" via the multi_select
                    // OR-intersection match the visibility engine supports.
                    visibleWhen: {
                        unforeseen_cost_categories: ['skatt_avgift_palaga', 'ombyggnadsatgarder'],
                    },
                    subClauses: [
                        {
                            ref: 'andelstal',
                            title: 'Lokalens andelstal',
                            visibleWhen: { unforeseen_cost_categories: 'ombyggnadsatgarder' },
                        },
                    ],
                },
                {
                    ref: 'moms',
                    title: 'Mervärdesskatt (moms)',
                    subClauses: [
                        { ref: 'tenant', title: 'Hyresgästens momsplikt' },
                        { ref: 'landlord', title: 'Hyresvärdens momsplikt' },
                    ],
                },
            ],
        },
        { ref: 'overlatelse', title: 'Överlåtelse' },
        { ref: 'storningar', title: 'Störningar och ordning' },
        { ref: 'tilltrade', title: 'Tillträde för hyresvärden' },
        { ref: 'forsakring', title: 'Försäkring' },
        { ref: 'konkurs', title: 'Konkurs och obestånd' },
        { ref: 'force-majeure', title: 'Force majeure' },
        { ref: 'andringar', title: 'Ändringar av avtalet' },
        { ref: 'lagval-tvist', title: 'Lagval och tvist' },
        { ref: 'bilagor', title: 'Bilagor' },
        { ref: 'underskrift', title: 'Underskrifter' },
    ],
    // Editor field-panel grouping. No `inputSections` → every section with
    // fields becomes its own standalone editor panel (one villkor per
    // panel), which is what we want here. Party sections (hyresvard/
    // hyresgast) and the property side of hyresobjekt are realised by the
    // dedicated parties/property panels; §12–26 are field-less except for
    // §13 underhall, which gets its own panel from its maintenance_* fields.
    notificationRules: lokalhyraNotificationRules,
    // Opt into per-party SigningBlock anchors. The send_for_signing
    // pipeline reads this and overlays signature / ort / datum on top of
    // the rendered PDF via Scrive placements. The same PDF still works
    // as a manual document — blank lines stay blank when handed off
    // out-of-band.
    signingPlacementAnchors: 'per-party-label',
};
/**
 * §9 Taxebundna tillägg — option constants shared across §9.1 (el) and
 * §9.2 (drift). Per Haymanot's "Taxebundna tillägg.docx".
 */
const EL_MODE_OPTIONS = [
    { value: 'eget_avtal', label: 'Eget avtal (tecknar avtal direkt med elleverantör och nätägare)', code: 'eget_avtal', summary: 'Tenant signs its own electricity contract directly with supplier and grid owner.' },
    { value: 'mataravlasning', label: 'Mätaravläsning (preliminärt tillägg, avräknas mot uppmätt förbrukning)', code: 'mataravlasning', summary: 'Preliminary rent supplement reconciled against metered electricity consumption.' },
    { value: 'andelstal', label: 'Andelstal (preliminärt tillägg utifrån lokalens andel av fastighetens totala yta)', code: 'andelstal', summary: 'Preliminary supplement based on the premises share of the property total area.' },
    { value: 'fast_uppraknings', label: 'Fast tillägg med årlig uppräkning', code: 'fast_uppraknings', summary: 'Fixed electricity supplement with an annual escalation.' },
];
const UPPRAKNINGS_MODELL_OPTIONS = [
    { value: 'fast_procent', label: 'Fast procentsats per år', code: 'fast_procent', summary: 'Escalated by a fixed percentage each year.' },
    { value: 'kpi', label: 'Konsumentprisindex (KPI)', code: 'kpi', summary: 'Escalated by the consumer price index (KPI).' },
    { value: 'faktisk_kostnad', label: 'Hyresvärdens faktiska kostnadsutveckling', code: 'faktisk_kostnad', summary: "Escalated by the landlord's actual cost development." },
];
/**
 * Fasta driftposter som ingår i tabellen för 9.2 (mätaravläsning /
 * andelstal / fast tillägg). Hyresgästens andel bestäms posten för post
 * via amount-fälten; tom/0 = posten ingår inte i tabellen.
 */
const DRIFT_TABLE_POSTS = [
    { key: 'varme', label: 'Värme' },
    { key: 'va', label: 'VA' },
    { key: 'kyla', label: 'Kyla' },
    { key: 'snorojning', label: 'Snöröjning' },
    { key: 'ventilation', label: 'Ventilation' },
    { key: 'fastighetsel', label: 'Fastighetsel' },
    { key: 'fastighetsskotsel', label: 'Fastighetsskötsel' },
];
/**
 * Driftposter i §9.2 (LH-13). Each may be assigned to exactly ONE category
 * (eget avtal / mätaravläsning / andelstal / endast bilaga). Shared by the
 * four per-category multi_select fields and the grouped Body render.
 */
const DRIFT_MEDIA_SUMMARIES = {
    varme: 'Heating operating cost.',
    va: 'Water and sewage (VA) operating cost.',
    kyla: 'Cooling operating cost.',
    snorojning: 'Snow clearance operating cost.',
    ventilation: 'Ventilation operating cost.',
    fastighetsel: 'Property/common electricity operating cost.',
    fastighetsskotsel: 'Property maintenance/caretaking operating cost.',
};
const DRIFT_MEDIA_OPTIONS = DRIFT_TABLE_POSTS.map((p) => ({
    value: p.key,
    label: p.label,
    code: p.key,
    summary: DRIFT_MEDIA_SUMMARIES[p.key] ?? `${p.label} operating cost.`,
}));
/** Label for a §9.2 media-type key (e.g. 'varme' → 'Värme'). */
const driftMediaLabel = (key) => DRIFT_TABLE_POSTS.find((p) => p.key === key)?.label ?? key;
export const variableSchema = [
    // Avtalsuppgifter — contract-level identifiers, top of the editor.
    {
        key: 'contract_number',
        label: 'Avtalsnummer',
        type: 'text',
        section: 'Avtalsuppgifter',
        requiredAtFinal: true,
        help: 'Egen referens på avtalet, t.ex. A-2042.',
        // Per-contract identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'object_number',
        label: 'Objektsnummer',
        type: 'text',
        section: 'Avtalsuppgifter',
        help: 'Valfritt — egen referens på hyresobjektet.',
        scopes: { copy: 'never' },
    },
    // §1 Hyresvärd / §2 Hyresgäst + Medhyresgäst — generated from
    // metadata.partyProjection (ADR-0065). Split address (gatuadress +
    // postnummeradress). The borgensman (säkerhet) is declared separately.
    ...expandPartyProjection(metadata),
    // §3 Hyresobjekt — property-linked
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'hyresobjekt',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
        scopes: { imported: { from: 'property.designation' } },
    },
    {
        key: 'property_address',
        label: 'Lokalens adress',
        type: 'textarea',
        section: 'hyresobjekt',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
        scopes: { imported: { from: 'property.composed_address', requiredForReview: true } },
    },
    {
        key: 'property_kommun',
        label: 'Kommun',
        type: 'text',
        section: 'hyresobjekt',
        autofill: { kind: 'property', field: 'kommun' },
    },
    {
        key: 'property_floor',
        label: 'Våningsplan',
        type: 'text',
        section: 'hyresobjekt',
        help: 'T.ex. 2 tr',
        scopes: { imported: { from: 'property.floor' } },
    },
    {
        key: 'size_description_mode',
        label: 'Beskrivning av lokalens storlek',
        type: 'select',
        section: 'hyresobjekt',
        requiredAtFinal: true,
        default: 'explicit_area',
        options: [
            { value: 'explicit_area', label: 'Ange yta (m²)', code: 'explicit_area', summary: 'Premises size is stated as an explicit area in square metres.' },
            {
                value: 'per_drawing',
                label: 'Hänvisa till bilagd ritning (yta valfri)',
                code: 'per_drawing',
                summary: 'Premises size is described by reference to an attached drawing; area optional.',
            },
        ],
    },
    {
        key: 'size_drawing_annex',
        label: 'Bilaga – handlingar',
        type: 'attachment_ref',
        section: 'hyresobjekt',
        requiredAtFinal: true,
        attachmentMultiple: true,
        attachmentDefaultName: 'Handlingar över lokalen',
        visibleWhen: { size_description_mode: 'per_drawing' },
        help: 'Välj eller ladda upp en eller flera handlingar som visar lokalens storlek och omfattning.',
    },
    {
        key: 'premises_area',
        label: 'Yta (m²)',
        type: 'number',
        min: 0,
        section: 'hyresobjekt',
        // Required only in 'explicit_area' mode; in 'per_drawing' mode the
        // ritning bilaga carries the size information and yta is optional.
        requiredAtFinal: false,
        scopes: { imported: { from: 'property.unit_size_sqm' } },
        help: 'I läget "Hänvisa till bilagd ritning" är yta valfri och skrivs ut endast om den anges.',
    },
    {
        key: 'premises_appurtenances',
        label: 'Tillhörande utrymmen',
        type: 'area_list',
        section: 'hyresobjekt',
        help: 'Lista tillhörande biutrymmen med yta, t.ex. förråd eller parkeringsplats.',
    },
    {
        key: 'include_planlosning',
        label: 'Bifoga planlösning',
        type: 'boolean',
        section: 'hyresobjekt',
        default: true,
        attachmentSlotUpload: 'bilaga_1_planlosning',
        help: 'Bilaga 1 reserveras för planlösningen — ladda upp ritningen nedan. Avmarkera om ingen planlösningsbilaga ska bifogas.',
    },
    // §4 Hyrestid och uppsägning
    {
        key: 'lease_start_date',
        label: 'Startdatum',
        type: 'date',
        section: 'hyrestid-uppsagning',
        requiredAtFinal: true,
        scopes: { imported: { from: 'dates.start', requiredForReview: true }, copy: 'manual' },
    },
    {
        key: 'lease_end_date',
        label: 'Slutdatum',
        type: 'date',
        section: 'hyrestid-uppsagning',
        durationFrom: 'lease_start_date',
        requiredAtFinal: true,
        scopes: { imported: { from: 'dates.end' }, copy: 'manual' },
    },
    {
        key: 'notice_months',
        label: 'Ömsesidig uppsägningstid (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'hyrestid-uppsagning',
        requiredAtFinal: true,
        help: 'T.ex. 9',
        scopes: { imported: { from: 'dates.notice_period_months' } },
    },
    {
        key: 'extension_months',
        label: 'Förlängning om ej uppsagt (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'hyrestid-uppsagning',
        requiredAtFinal: true,
        help: 'T.ex. 12',
        scopes: { imported: { from: 'dates.auto_extension_months' } },
    },
    // §5 Lokalens användning
    {
        key: 'premises_usage',
        label: 'Lokalens ändamål',
        type: 'textarea',
        section: 'anvandning',
        requiredAtFinal: true,
        help: 'Beskriv verksamhetens art, t.ex. butikslokal för detaljhandel',
    },
    // §6 Lokalens skick
    {
        key: 'premises_condition',
        label: 'Lokalen hyrs ut i',
        type: 'select',
        section: 'skick',
        requiredAtFinal: true,
        help: 'Tilläggsavtal för hyresgästanpassning skapas som ett kopplat avtal (under ”Kopplade avtal” högst upp), inte som bilaga. Bilagan här specificerar enbart lokalens anpassade skick.',
        options: [
            { value: 'Befintligt skick utan för verksamheten avsedd inredning', label: 'Befintligt skick utan för verksamheten avsedd inredning', code: 'as_is_no_fitout', summary: 'Premises let in existing condition, without fit-out for the business.' },
            { value: 'Befintligt skick med för verksamheten särskilt anpassat skick enligt bilaga', label: 'Befintligt skick med för verksamheten särskilt anpassat skick enligt bilaga', code: 'as_is_adapted_per_annex', summary: 'Existing condition plus business-specific adaptation specified in an annex.' },
            { value: 'För verksamheten särskilt anpassat skick och avsedd inredning enligt bilaga', label: 'För verksamheten särskilt anpassat skick och avsedd inredning enligt bilaga', code: 'adapted_with_fitout_per_annex', summary: 'Business-specific adapted condition and fit-out specified in an annex.' },
        ],
    },
    {
        key: 'condition_annex',
        label: 'Bilaga för lokalens skick',
        type: 'attachment_ref',
        section: 'skick',
        requiredAtFinal: true,
        attachmentMultiple: true,
        attachmentDefaultName: 'Lokalens skick',
        // Recommendations surfaced as "Skapa från mall" buttons in the
        // create-bilaga dialog (in declaration order). Manual PDF upload
        // remains as the fallback.
        recommendedAttachmentTemplate: [
            { key: 'indexavtal', version: 'v1' },
        ],
        visibleWhen: {
            premises_condition: [
                'Befintligt skick med för verksamheten särskilt anpassat skick enligt bilaga',
                'För verksamheten särskilt anpassat skick och avsedd inredning enligt bilaga',
            ],
        },
        help: 'Välj eller skapa en eller flera bilagor som specificerar lokalens anpassade skick.',
    },
    // Optional besiktnings- och åtgärdsprotokoll (the "optional ruta").
    {
        key: 'inspection_protocol_included',
        label: 'Bifoga besiktnings- och åtgärdsprotokoll',
        type: 'boolean',
        section: 'skick',
        help: 'Lägg till en beskrivning av lokalens skick vid avtalets ingående via ett besiktnings- och åtgärdsprotokoll som bilaga.',
    },
    {
        key: 'inspection_protocol_annex',
        label: 'Bilaga för besiktnings- och åtgärdsprotokoll',
        type: 'attachment_ref',
        section: 'skick',
        requiredAtFinal: true,
        attachmentDefaultName: 'Besiktnings- och åtgärdsprotokoll',
        visibleWhen: { inspection_protocol_included: true },
        help: 'Välj eller skapa besiktnings- och åtgärdsprotokollet.',
    },
    // §7 Hyra
    {
        key: 'rent_amount',
        label: 'Årshyra (kr/år)',
        type: 'currency',
        min: 0,
        section: 'hyra',
        requiredAtFinal: true,
        scopes: { imported: { from: 'rent.yearly_sek', requiredForReview: true }, copy: 'manual' },
    },
    {
        key: 'payment_frequency',
        label: 'Hyran aviseras',
        type: 'select',
        section: 'hyra',
        requiredAtFinal: true,
        default: 'kvartalsvis',
        options: [
            { value: 'månadsvis', label: 'Månadsvis', code: 'manadsvis', summary: 'Rent is invoiced monthly.' },
            { value: 'kvartalsvis', label: 'Kvartalsvis', code: 'kvartalsvis', summary: 'Rent is invoiced quarterly.' },
            { value: 'årsvis', label: 'Årsvis', code: 'arsvis', summary: 'Rent is invoiced yearly.' },
        ],
    },
    {
        key: 'payment_type',
        label: 'Betalningssätt',
        type: 'select',
        section: 'hyra',
        requiredAtFinal: true,
        default: 'Enligt avisering i faktura',
        options: [
            { value: 'Enligt avisering i faktura', label: 'Enligt avisering i faktura (rekommenderas)', code: 'per_invoice', summary: 'Paid per the invoice/notice issued by the landlord (recommended).' },
            { value: 'Plusgiro', label: 'Plusgiro', code: 'plusgiro', summary: 'Paid to a plusgiro account number.' },
            { value: 'Bankgiro', label: 'Bankgiro', code: 'bankgiro', summary: 'Paid to a bankgiro account number.' },
        ],
    },
    {
        key: 'payment_account',
        label: 'Hyresvärdens konto (BG/PG + nummer)',
        type: 'text',
        section: 'hyra',
        help: 'T.ex. BG 123-4567',
        visibleWhen: { payment_type: ['Plusgiro', 'Bankgiro'] },
    },
    // §8 Hyrans årsvisa höjning
    {
        key: 'rent_increase_model',
        label: 'Hyreshöjningsmodell',
        type: 'select',
        section: 'arlig-hojning',
        requiredAtFinal: true,
        options: [
            { value: 'Indexreglering (KPI, hyrestid ≥ 3 år)', label: 'Indexreglering (KPI, hyrestid ≥ 3 år)', code: 'kpi_index', summary: 'Rent indexed to KPI; standard for leases of three years or more.' },
            { value: 'Fast höjning (hyrestid 1–3 år)', label: 'Fast höjning (hyrestid 1–3 år)', code: 'fixed_increase', summary: 'Fixed annual increase; for leases of one to three years.' },
            { value: 'Indexreglering enligt bilaga', label: 'Indexreglering enligt bilaga', code: 'index_per_annex', summary: 'Indexation governed by a separate index clause annex.' },
        ],
    },
    {
        key: 'rent_index_annex',
        label: 'Bilaga – indexregleringsklausul',
        type: 'attachment_ref',
        section: 'arlig-hojning',
        requiredAtFinal: true,
        attachmentDefaultName: 'Indexavtal',
        recommendedAttachmentTemplate: { key: 'indexavtal', version: 'v1' },
        visibleWhen: { rent_increase_model: 'Indexreglering enligt bilaga' },
        help: 'Välj eller skapa en indexregleringsbilaga (mall: Indexavtal).',
    },
    {
        key: 'rent_fixed_increase',
        label: 'Fast höjningsvillkor',
        type: 'textarea',
        section: 'arlig-hojning',
        help: 'Beskriv villkoren för hyreshöjning',
        visibleWhen: { rent_increase_model: 'Fast höjning (hyrestid 1–3 år)' },
    },
    {
        key: 'rent_base_amount',
        label: 'Bashyra (kr eller %)',
        type: 'text',
        section: 'arlig-hojning',
        help: 'T.ex. 200 000 kr eller 100 %',
        visibleWhen: { rent_increase_model: 'Indexreglering (KPI, hyrestid ≥ 3 år)' },
    },
    {
        key: 'rent_index_base_year',
        label: 'Avvikande bastal (år, om annat än standard)',
        type: 'text',
        section: 'arlig-hojning',
        help: 'Lämna tomt för standardbastal (2020)',
        visibleWhen: { rent_increase_model: 'Indexreglering (KPI, hyrestid ≥ 3 år)' },
    },
    {
        // LH-11: opt into a fully manual index clause. When on, the standard
        // KPI clause body is replaced by the author's own free-text.
        key: 'rent_index_manual',
        label: 'Skriv indexregleringsklausulen helt manuellt',
        type: 'boolean',
        section: 'arlig-hojning',
        help: 'Markera för att ersätta standardtexten med en egen formulering av indexregleringsklausulen.',
        visibleWhen: { rent_increase_model: 'Indexreglering (KPI, hyrestid ≥ 3 år)' },
    },
    {
        key: 'rent_index_manual_text',
        label: 'Indexregleringsklausul (egen text)',
        type: 'textarea',
        section: 'arlig-hojning',
        requiredAtFinal: true,
        visibleWhen: {
            rent_increase_model: 'Indexreglering (KPI, hyrestid ≥ 3 år)',
            rent_index_manual: true,
        },
        help: 'Skriv hela indexregleringsklausulen som den ska visas i avtalet.',
    },
    // §9.1 Ersättning för el — 4-way alternative.
    {
        key: 'el_mode',
        label: 'Modell – el',
        type: 'select',
        section: 'forbrukning.el',
        requiredAtFinal: true,
        default: 'mataravlasning',
        options: EL_MODE_OPTIONS,
    },
    // 9.1 mataravlasning parameters
    {
        key: 'el_meter_preliminary_yearly',
        label: 'Preliminär ersättning vid avtalets ingående (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.el',
        visibleWhen: { el_mode: 'mataravlasning' },
    },
    {
        key: 'el_meter_andel_percent',
        label: 'Lokalens andel av Fastighetens totala uthyrbara yta (%)',
        type: 'text',
        section: 'forbrukning.el',
        visibleWhen: { el_mode: 'mataravlasning' },
        help: 'Används om förbrukning inte kan mätas separat med undermätare.',
    },
    // 9.1 andelstal parameters
    {
        key: 'el_andelstal_percent',
        label: 'Lokalens andel av Fastighetens totala uthyrbara yta (%)',
        type: 'text',
        section: 'forbrukning.el',
        visibleWhen: { el_mode: 'andelstal' },
    },
    {
        key: 'el_andelstal_yearly',
        label: 'Motsvarande preliminärt hyrestillägg vid avtalets ingående (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.el',
        visibleWhen: { el_mode: 'andelstal' },
    },
    // 9.1 fast tillägg med årlig uppräkning
    {
        key: 'el_fast_yearly',
        label: 'Fast hyrestillägg vid avtalets ingående (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.el',
        visibleWhen: { el_mode: 'fast_uppraknings' },
    },
    {
        key: 'el_fast_uppraknings_modell',
        label: 'Modell för årlig uppräkning',
        type: 'select',
        section: 'forbrukning.el',
        default: 'fast_procent',
        options: UPPRAKNINGS_MODELL_OPTIONS,
        visibleWhen: { el_mode: 'fast_uppraknings' },
    },
    {
        key: 'el_fast_procent_per_year',
        label: 'Procentsats per år (%)',
        type: 'text',
        section: 'forbrukning.el',
        visibleWhen: {
            el_mode: 'fast_uppraknings',
            el_fast_uppraknings_modell: 'fast_procent',
        },
    },
    {
        key: 'el_fast_kpi_base_month_year',
        label: 'Bastal (månad år, t.ex. oktober 2024)',
        type: 'text',
        section: 'forbrukning.el',
        visibleWhen: {
            el_mode: 'fast_uppraknings',
            el_fast_uppraknings_modell: 'kpi',
        },
    },
    {
        key: 'el_fast_kpi_comparison_month',
        label: 'Jämförelsetal — månad närmast före uppräkningstidpunkten',
        type: 'text',
        section: 'forbrukning.el',
        visibleWhen: {
            el_mode: 'fast_uppraknings',
            el_fast_uppraknings_modell: 'kpi',
        },
        help: 't.ex. oktober',
    },
    {
        key: 'el_fast_faktisk_notice_date',
        label: 'Senaste skriftliga aviseringsdatum varje år',
        type: 'text',
        section: 'forbrukning.el',
        visibleWhen: {
            el_mode: 'fast_uppraknings',
            el_fast_uppraknings_modell: 'faktisk_kostnad',
        },
        help: 't.ex. 30 april',
    },
    // §9.2 Ersättning för värme, kyla, drift och skötsel m.m.
    //
    // LH-13/LH-14: inverted per-media-type assignment. Each media type is
    // placed in exactly one category. The four multi_selects share the same
    // option set; cross-field exclusivity is enforced editor-side
    // (NEEDS ANDRÉ VALIDATION) — the Body dedupes by precedence as a guard.
    {
        key: 'drift_eget_posts',
        label: 'Eget avtal — driftposter hyresgästen tecknar eget avtal för',
        type: 'multi_select',
        section: 'forbrukning.drift',
        options: DRIFT_MEDIA_OPTIONS,
        mutexGroup: 'drift',
        help: 'Hyresgästen tecknar eget avtal direkt för dessa driftposter.',
    },
    {
        key: 'drift_meter_posts',
        label: 'Mätaravläsning — driftposter som avräknas mot uppmätt förbrukning',
        type: 'multi_select',
        section: 'forbrukning.drift',
        options: DRIFT_MEDIA_OPTIONS,
        mutexGroup: 'drift',
        help: 'Preliminärt hyrestillägg som avräknas mot uppmätt förbrukning/andel.',
    },
    {
        key: 'drift_andel_posts',
        label: 'Andelstal — driftposter utifrån lokalens andel av Fastigheten',
        type: 'multi_select',
        section: 'forbrukning.drift',
        options: DRIFT_MEDIA_OPTIONS,
        mutexGroup: 'drift',
        help: 'Preliminärt hyrestillägg utifrån lokalens andel av Fastighetens totala yta.',
    },
    {
        key: 'drift_bilaga_posts',
        label: 'Endast bilaga — driftposter som regleras i driftskostnadsklausul-bilaga',
        type: 'multi_select',
        section: 'forbrukning.drift',
        options: DRIFT_MEDIA_OPTIONS,
        mutexGroup: 'drift',
        help: 'Dessa driftposter definieras enbart i den bifogade driftskostnadsklausulen (se nedan).',
    },
    // Per-post current cost (kr/år) — used by the mätaravläsning table (with
    // total) and the andelstal cost list. Shown in the panel regardless of
    // assignment; the Body renders a cost only for posts placed under
    // mätaravläsning or andelstal. (NEEDS ANDRÉ VALIDATION: editor noise.)
    {
        key: 'drift_amount_varme',
        label: 'Värme (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.drift',
        // Only relevant when the post is priced (mätaravläsning or andelstal).
        visibleWhen: { anyOf: [{ drift_meter_posts: 'varme' }, { drift_andel_posts: 'varme' }] },
        help: 'Anges för de driftposter du placerat under mätaravläsning eller andelstal.',
    },
    {
        key: 'drift_amount_va',
        label: 'VA (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.drift',
        visibleWhen: { anyOf: [{ drift_meter_posts: 'va' }, { drift_andel_posts: 'va' }] },
    },
    {
        key: 'drift_amount_kyla',
        label: 'Kyla (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.drift',
        visibleWhen: { anyOf: [{ drift_meter_posts: 'kyla' }, { drift_andel_posts: 'kyla' }] },
    },
    {
        key: 'drift_amount_snorojning',
        label: 'Snöröjning (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.drift',
        visibleWhen: { anyOf: [{ drift_meter_posts: 'snorojning' }, { drift_andel_posts: 'snorojning' }] },
    },
    {
        key: 'drift_amount_ventilation',
        label: 'Ventilation (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.drift',
        visibleWhen: { anyOf: [{ drift_meter_posts: 'ventilation' }, { drift_andel_posts: 'ventilation' }] },
    },
    {
        key: 'drift_amount_fastighetsel',
        label: 'Fastighetsel (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.drift',
        visibleWhen: { anyOf: [{ drift_meter_posts: 'fastighetsel' }, { drift_andel_posts: 'fastighetsel' }] },
    },
    {
        key: 'drift_amount_fastighetsskotsel',
        label: 'Fastighetsskötsel (kr/år)',
        type: 'currency',
        min: 0,
        section: 'forbrukning.drift',
        visibleWhen: { anyOf: [{ drift_meter_posts: 'fastighetsskotsel' }, { drift_andel_posts: 'fastighetsskotsel' }] },
    },
    // andel %: lokalens andel av Fastighetens totala uthyrbara yta. Shown
    // when at least one media type is placed under mätaravläsning or andelstal.
    {
        key: 'drift_andel_percent',
        label: 'Lokalens andel av Fastighetens totala uthyrbara yta (%)',
        type: 'text',
        section: 'forbrukning.drift',
        help: 'Anges vid mätaravläsning eller andelstal — t.ex. 8.',
    },
    // LH-14: "övrig driftkostnad" → driftskostnadsklausul-bilaga. Required
    // when ≥1 media type is placed under "Endast bilaga"; otherwise optional.
    {
        key: 'drift_annex',
        label: 'Bilaga – driftskostnadsklausul',
        type: 'attachment_ref',
        section: 'forbrukning.drift',
        attachmentDefaultName: 'Driftskostnadsklausul',
        // Required once any driftpost is placed under "Endast bilaga". The rule
        // matches when drift_bilaga_posts (a multi_select array) contains any
        // option value (array-actual ∩ array-expected).
        requiredWhen: { drift_bilaga_posts: DRIFT_MEDIA_OPTIONS.map((o) => o.value) },
        help: 'Reglerar övriga driftkostnader. Obligatorisk om någon driftpost valts som "Endast bilaga".',
    },
    // §10 Avfallshantering
    {
        key: 'waste_compactor_allowed',
        label: 'Hyresgästen får nyttja Fastighetens komprimatorer',
        type: 'boolean',
        section: 'avfallshantering',
        help: 'Valfritt — markera om Fastigheten har komprimatorer som hyresgästen får använda.',
    },
    {
        key: 'waste_transport_mode',
        label: 'Borttransport av avfall',
        type: 'select',
        section: 'avfallshantering',
        requiredAtFinal: true,
        default: 'tenant_own',
        options: [
            { value: 'tenant_own', label: 'Hyresgästen svarar och bekostar borttransport', code: 'tenant_own', summary: 'Tenant arranges and pays for waste removal itself.' },
            {
                value: 'landlord_coordinated',
                label: 'Hyresvärden samordnar (andel + kostnadstillägg)',
                code: 'landlord_coordinated',
                summary: 'Landlord coordinates removal; tenant pays a share as a cost supplement.',
            },
            { value: 'included_in_rent', label: 'Ingår i hyran', code: 'included_in_rent', summary: 'Waste removal cost is included in the rent.' },
            { value: 'per_annex', label: 'Särskild reglering enligt bilaga', code: 'per_annex', summary: 'Waste handling governed by a separate annex.' },
        ],
    },
    {
        key: 'waste_share_percent',
        label: 'Lokalens andel av avfallskostnaden (%)',
        type: 'text',
        section: 'avfallshantering',
        visibleWhen: { waste_transport_mode: 'landlord_coordinated' },
        help: 't.ex. 12',
    },
    {
        key: 'waste_yearly_amount',
        label: 'Hyrestillägg för avfall vid avtalets tecknande (kr/år)',
        type: 'currency',
        min: 0,
        section: 'avfallshantering',
        visibleWhen: { waste_transport_mode: 'landlord_coordinated' },
    },
    {
        key: 'waste_annex',
        label: 'Bilaga – avfallshantering',
        type: 'attachment_ref',
        section: 'avfallshantering',
        requiredAtFinal: true,
        attachmentDefaultName: 'Avfallshantering',
        visibleWhen: { waste_transport_mode: ['landlord_coordinated', 'per_annex'] },
        help: 'Bilagan beskriver fördelningen / den särskilda regleringen.',
    },
    // §11 Bevakning
    {
        key: 'bevakning_mode',
        label: 'Bevakning av Fastigheten',
        type: 'select',
        section: 'bevakning',
        requiredAtFinal: true,
        default: 'tenant_own',
        options: [
            { value: 'tenant_own', label: 'Hyresvärden ombesörjer inte bevakning', code: 'tenant_own', summary: 'Landlord does not provide security/surveillance; tenant handles its own.' },
            {
                value: 'landlord_provides',
                label: 'Hyresvärden tillhandahåller bevakning (andel + kostnadstillägg)',
                code: 'landlord_provides',
                summary: 'Landlord provides security; tenant pays a share as a cost supplement.',
            },
        ],
    },
    {
        key: 'bevakning_share_percent',
        label: 'Lokalens andel av bevakningskostnaden (%)',
        type: 'text',
        section: 'bevakning',
        visibleWhen: { bevakning_mode: 'landlord_provides' },
        help: 't.ex. 12',
    },
    {
        key: 'bevakning_yearly_amount',
        label: 'Hyrestillägg för bevakning vid avtalets tecknande (kr/år)',
        type: 'currency',
        min: 0,
        section: 'bevakning',
        visibleWhen: { bevakning_mode: 'landlord_provides' },
    },
    // §10 Säkerhet
    {
        key: 'security_type',
        label: 'Säkerhetstyp',
        type: 'select',
        section: 'sakerhet',
        requiredAtFinal: true,
        options: [
            { value: 'Ingen säkerhet', label: 'Ingen säkerhet', code: 'ingen', summary: 'No security is provided for the lease.' },
            { value: 'Deposition', label: 'Deposition', code: 'deposition', summary: 'Cash deposit held as security.' },
            { value: 'Bankgaranti', label: 'Bankgaranti', code: 'bankgaranti', summary: 'Bank guarantee as security.' },
            { value: 'Hyresgarantiförsäkring (individuell)', label: 'Hyresgarantiförsäkring (individuell)', code: 'hyresgaranti_individuell', summary: 'Individual rent-guarantee insurance as security.' },
            { value: 'Hyresgarantiförsäkring (gruppförsäkring)', label: 'Hyresgarantiförsäkring (gruppförsäkring)', code: 'hyresgaranti_grupp', summary: 'Group rent-guarantee insurance as security.' },
            { value: 'Proprieborgen', label: 'Proprieborgen', code: 'proprieborgen', summary: 'Surety bond (proprieborgen) from a guarantor.' },
        ],
    },
    {
        key: 'security_months',
        label: 'Antal månadshyror',
        type: 'number',
        min: 0,
        integer: true,
        section: 'sakerhet',
        help: 'T.ex. 3',
        visibleWhen: { security_type: 'Deposition' },
    },
    {
        key: 'security_amount',
        label: 'Belopp (kr)',
        type: 'currency',
        min: 0,
        section: 'sakerhet',
        visibleWhen: { security_type: 'Deposition' },
    },
    {
        key: 'security_account',
        label: 'Depositionskonto (BG/PG + nummer)',
        type: 'text',
        section: 'sakerhet',
        help: 'Fyll i vid deposition',
        visibleWhen: { security_type: 'Deposition' },
    },
    {
        key: 'insurance_premium',
        label: 'Försäkringspremie (kr/år)',
        type: 'currency',
        min: 0,
        section: 'sakerhet',
        help: 'Fyll i vid hyresgarantiförsäkring',
        visibleWhen: {
            security_type: [
                'Hyresgarantiförsäkring (individuell)',
                'Hyresgarantiförsäkring (gruppförsäkring)',
            ],
        },
    },
    // Borgensman — autofilled from (guarantor, 0); visible when security_type is Proprieborgen.
    {
        key: 'guarantor_name',
        label: 'Borgensman – namn',
        type: 'text',
        section: 'sakerhet',
        visibleWhen: { security_type: 'Proprieborgen' },
        autofill: { kind: 'party', role: 'guarantor', ordinal: 0, field: 'legal_name' },
    },
    {
        key: 'guarantor_org_number',
        label: 'Borgensman – personnr/org.nr',
        type: 'text',
        section: 'sakerhet',
        visibleWhen: { security_type: 'Proprieborgen' },
        autofill: { kind: 'party', role: 'guarantor', ordinal: 0, field: 'id_number' },
    },
    {
        key: 'guarantor_address',
        label: 'Borgensman – adress',
        type: 'textarea',
        section: 'sakerhet',
        visibleWhen: { security_type: 'Proprieborgen' },
        autofill: { kind: 'party', role: 'guarantor', ordinal: 0, field: 'composed_address' },
    },
    {
        key: 'guarantor_phone',
        label: 'Borgensman – telefon',
        type: 'text',
        section: 'sakerhet',
        visibleWhen: { security_type: 'Proprieborgen' },
        autofill: { kind: 'party', role: 'guarantor', ordinal: 0, field: 'phone' },
    },
    {
        key: 'guarantor_email',
        label: 'Borgensman – e-post',
        type: 'text',
        section: 'sakerhet',
        visibleWhen: { security_type: 'Proprieborgen' },
        autofill: { kind: 'party', role: 'guarantor', ordinal: 0, field: 'email' },
    },
    {
        key: 'guarantor_end_date',
        label: 'Borgensåtagande – slutdatum',
        type: 'date',
        section: 'sakerhet',
        visibleWhen: { security_type: 'Proprieborgen' },
    },
    // §11 Besittningsskydd
    {
        key: 'possession_protection',
        label: 'Besittningsskydd',
        type: 'select',
        section: 'besittningsskydd',
        requiredAtFinal: true,
        default: 'Hyresgästen har besittningsskydd',
        options: [
            { value: 'Hyresgästen har besittningsskydd', label: 'Hyresgästen har besittningsskydd', code: 'has_protection', summary: 'Tenant retains statutory security of tenure (besittningsskydd).' },
            { value: 'Hyresgästen avstår från besittningsskydd – godkänt av hyresnämnden', label: 'Hyresgästen avstår – godkänt av hyresnämnden', code: 'waived_approved', summary: 'Tenant waives security of tenure, approved by the rent tribunal.' },
            { value: 'Hyresgästen avstår från besittningsskydd – ej föremål för prövning', label: 'Hyresgästen avstår – ej föremål för prövning', code: 'waived_no_review', summary: 'Tenant waives security of tenure; not subject to tribunal review.' },
        ],
    },
    {
        key: 'possession_waiver_from',
        label: 'Avståendets giltighetstid fr.o.m.',
        type: 'date',
        section: 'besittningsskydd',
        visibleWhen: {
            possession_protection: [
                'Hyresgästen avstår från besittningsskydd – godkänt av hyresnämnden',
                'Hyresgästen avstår från besittningsskydd – ej föremål för prövning',
            ],
        },
    },
    {
        key: 'possession_waiver_to',
        label: 'Avståendets giltighetstid t.o.m.',
        type: 'date',
        section: 'besittningsskydd',
        visibleWhen: {
            possession_protection: [
                'Hyresgästen avstår från besittningsskydd – godkänt av hyresnämnden',
                'Hyresgästen avstår från besittningsskydd – ej föremål för prövning',
            ],
        },
    },
    {
        key: 'besittningsskydd_connected_note',
        label: 'Skapa avståendet som kopplat avtal',
        type: 'note',
        section: 'besittningsskydd',
        visibleWhen: {
            possession_protection: [
                'Hyresgästen avstår från besittningsskydd – godkänt av hyresnämnden',
                'Hyresgästen avstår från besittningsskydd – ej föremål för prövning',
            ],
        },
        help: 'Överenskommelsen om avstående från besittningsskydd upprättas som ett separat, kopplat avtal med egen signering – inte som bilaga. Skapa det under "Kopplade avtal" högst upp. Rekommenderas som ett sista steg, när övriga uppgifter i avtalet är ifyllda.',
    },
    // §14 Lokalens lås- och stöldskyddsanordningar.
    //
    // Editor-panel order: this input block is intentionally placed directly
    // after Besittningsskydd (LH-15, 2026-05-31) so the §14 editor panel
    // follows §13 in the form. The document RENDER order is unchanged — the
    // <Section sectionRef="las-stold"> stays in its §14 slot via metadata.sections.
    {
        key: 'lock_responsibility',
        label: 'Lås- och stöldskyddsanordningar',
        type: 'select',
        section: 'las-stold',
        requiredAtFinal: true,
        default: 'Hyresgästen ansvarar',
        options: [
            { value: 'Hyresgästen ansvarar', label: 'Hyresgästen ansvarar', code: 'tenant', summary: 'Tenant is responsible for locks and theft-protection devices.' },
            { value: 'Hyresvärden ansvarar', label: 'Hyresvärden ansvarar', code: 'landlord', summary: 'Landlord is responsible for locks and theft-protection devices.' },
            { value: 'Enligt särskilda villkor i bilaga', label: 'Enligt särskilda villkor i bilaga', code: 'per_annex', summary: 'Locks/theft-protection responsibility governed by a separate annex.' },
        ],
    },
    {
        key: 'lock_annex',
        label: 'Bilaga för lås- och stöldskydd',
        type: 'attachment_ref',
        section: 'las-stold',
        requiredAtFinal: true,
        attachmentDefaultName: 'Lås- och stöldskyddsanordningar',
        visibleWhen: { lock_responsibility: 'Enligt särskilda villkor i bilaga' },
        help: 'Välj eller skapa bilagan med särskilda villkor om lås och stöldskydd.',
    },
    // §16 Ombyggnads- och ändringsarbeten (per "Ombyggnads- och
    // ändringsarbeten + skyltning.docx").
    //
    // Editor-panel order: this input block is intentionally placed after
    // Besittningsskydd / Lås- och stöldskydd (LH-8, 2026-05-30) so the §16
    // editor panel follows §13–14 in the form. The document RENDER order is
    // unchanged — <Section sectionRef="ombyggnad"> stays in its §16 slot via
    // metadata.sections.
    {
        key: 'byggvarudeklaration_included',
        label: 'Inkludera klausul om byggvarudeklaration',
        type: 'boolean',
        section: 'ombyggnad',
        help: 'Valbar. Markera om hyresgästen ska förete byggvarudeklarationer i god tid före arbete.',
    },
    {
        key: 'tillaggsavtal_hyresgastanpassning_connected_note',
        label: 'Skapa hyresgästanpassning som kopplat avtal',
        type: 'note',
        section: 'ombyggnad',
        help: 'Hyresgästanpassning (investeringsbidrag eller ombyggnad) regleras i ett separat, kopplat tilläggsavtal med egen signering – inte som bilaga. Skapa det under "Kopplade avtal" högst upp. Parternas uppgifter och lokalens adress förifylls från lokalavtalet.',
    },
    {
        key: 'sedvanligt_underhall_alt',
        label: 'Nedsättning av hyra vid sedvanligt underhåll',
        type: 'select',
        section: 'ombyggnad.nedsattning.sedvanligt-underhall',
        requiredAtFinal: true,
        default: 'A',
        options: [
            { value: 'A', label: 'Alt. A — ingen nedsättning (rekommenderas)', code: 'no_reduction', summary: 'No rent reduction for ordinary maintenance disturbance (recommended).' },
            { value: 'B', label: 'Alt. B — nedsättning enligt hyreslagens regler', code: 'statutory_reduction', summary: 'Rent reduction follows the rules of the Tenancy Act.' },
            { value: 'C', label: 'Alt. C — regleras enligt särskild bilaga', code: 'per_annex', summary: 'Rent-reduction right governed by a separate annex.' },
        ],
    },
    {
        key: 'sedvanligt_underhall_annex',
        label: 'Bilaga – nedsättning vid sedvanligt underhåll',
        type: 'attachment_ref',
        section: 'ombyggnad.nedsattning.sedvanligt-underhall',
        requiredAtFinal: true,
        attachmentDefaultName: 'Nedsättning vid sedvanligt underhåll',
        visibleWhen: { sedvanligt_underhall_alt: 'C' },
        help: 'Välj eller skapa bilagan som reglerar rätten till nedsättning.',
    },
    // §13 Underhållsansvar (underhållsklausul.docx)
    // 13.1 Lokalen
    {
        key: 'maintenance_premises',
        label: 'Underhåll av lokalen',
        type: 'select',
        section: 'underhall.lokalen',
        requiredAtFinal: true,
        default: 'Hyresvärden ansvarar för underhåll av lokalen',
        options: [
            { value: 'Hyresvärden ansvarar för underhåll av lokalen', label: 'Hyresvärden ansvarar för underhåll av lokalen', code: 'landlord', summary: 'Landlord is responsible for maintenance of the premises.' },
            { value: 'Hyresgästen ansvarar för underhåll av ytskikt och inredning', label: 'Hyresgästen ansvarar för underhåll av ytskikt och inredning', code: 'tenant_surfaces', summary: 'Tenant is responsible for maintaining surfaces and fixtures.' },
            { value: 'Annan fördelning enligt bilaga (gränsdragningslista)', label: 'Annan fördelning enligt bilaga (gränsdragningslista)', code: 'per_annex', summary: 'Maintenance split governed by an annexed demarcation list.' },
        ],
    },
    {
        key: 'maintenance_premises_annex',
        label: 'Bilaga – gränsdragningslista (lokalen)',
        type: 'attachment_ref',
        section: 'underhall.lokalen',
        requiredAtFinal: true,
        attachmentDefaultName: 'Gränsdragningslista',
        visibleWhen: { maintenance_premises: 'Annan fördelning enligt bilaga (gränsdragningslista)' },
        help: 'Välj eller skapa gränsdragningslistan för underhållsansvaret.',
    },
    // 13.2 Allmänna och gemensamma utrymmen
    {
        key: 'maintenance_common',
        label: 'Allmänna och gemensamma utrymmen',
        type: 'select',
        section: 'underhall.gemensamma',
        requiredAtFinal: true,
        default: 'Hyresvärden ansvarar',
        options: [
            { value: 'Hyresvärden ansvarar', label: 'Hyresvärden ansvarar (standard)', code: 'landlord', summary: 'Landlord is responsible for common areas (standard).' },
            { value: 'Annan överenskommelse enligt bilaga', label: 'Annan överenskommelse enligt bilaga', code: 'per_annex', summary: 'Common-area maintenance governed by a separate annex.' },
        ],
    },
    {
        key: 'maintenance_common_annex',
        label: 'Bilaga – allmänna/gemensamma utrymmen',
        type: 'attachment_ref',
        section: 'underhall.gemensamma',
        requiredAtFinal: true,
        attachmentDefaultName: 'Underhåll av allmänna och gemensamma utrymmen',
        visibleWhen: { maintenance_common: 'Annan överenskommelse enligt bilaga' },
        help: 'Välj eller skapa bilagan med den avvikande överenskommelsen.',
    },
    // 13.3 Tillhörande biutrymmen — fristående tilläggsparagraf. The Word
    // intends this to auto-activate when biutrymmen are part of the
    // hyresobjekt; visibleWhen can't test the free-text appurtenances
    // field for non-emptiness, so we expose an explicit toggle.
    {
        key: 'ancillary_spaces_maintenance',
        label: 'Reglera underhåll av tillhörande biutrymmen',
        type: 'boolean',
        section: 'underhall',
        help: 'Aktiverar tilläggsparagraf 13.3. Markera om hyresobjektet omfattar tillhörande biutrymmen, t.ex. förråd.',
    },
    {
        key: 'maintenance_ancillary',
        label: 'Underhåll av biutrymmen',
        type: 'select',
        section: 'underhall.biutrymmen',
        requiredAtFinal: true,
        default: 'Hyresvärden ansvarar för underhåll av biutrymmen',
        visibleWhen: { ancillary_spaces_maintenance: true },
        options: [
            { value: 'Hyresvärden ansvarar för underhåll av biutrymmen', label: 'Hyresvärden ansvarar för underhåll av biutrymmen', code: 'landlord', summary: 'Landlord is responsible for maintenance of ancillary spaces.' },
            { value: 'Hyresgästen ansvarar för underhåll av biutrymmen', label: 'Hyresgästen ansvarar för underhåll av biutrymmen', code: 'tenant', summary: 'Tenant is responsible for maintenance of ancillary spaces.' },
            { value: 'Delat underhållsansvar', label: 'Delat underhållsansvar', code: 'shared', summary: 'Maintenance of ancillary spaces is shared between the parties.' },
            { value: 'Annan fördelning enligt bilaga (gränsdragningslista)', label: 'Annan fördelning enligt bilaga (gränsdragningslista)', code: 'per_annex', summary: 'Ancillary-space maintenance split governed by an annexed demarcation list.' },
        ],
    },
    {
        key: 'maintenance_ancillary_annex',
        label: 'Bilaga – gränsdragningslista (biutrymmen)',
        type: 'attachment_ref',
        section: 'underhall.biutrymmen',
        requiredAtFinal: true,
        attachmentDefaultName: 'Gränsdragningslista biutrymmen',
        visibleWhen: {
            ancillary_spaces_maintenance: true,
            maintenance_ancillary: 'Annan fördelning enligt bilaga (gränsdragningslista)',
        },
        help: 'Välj eller skapa gränsdragningslistan för biutrymmenas underhåll.',
    },
    // §15 Skyltning
    {
        key: 'skyltprogram_included',
        label: 'Hyresgästen förbinder sig att följa bifogat skyltprogram',
        type: 'boolean',
        section: 'skyltning',
        help: 'Valbar. Markera om ett skyltprogram bifogas som hyresgästen ska följa.',
    },
    {
        key: 'skyltprogram_annex',
        label: 'Bilaga – skyltprogram',
        type: 'attachment_ref',
        section: 'skyltning',
        requiredAtFinal: true,
        attachmentDefaultName: 'Skyltprogram',
        visibleWhen: { skyltprogram_included: true },
        help: 'Välj eller skapa skyltprogrammet.',
    },
    // Brandskydd (per Brandskyddsklausul.docx)
    {
        key: 'brandskydd_initial_mode',
        label: 'Ansvar för brandskydd vid tillträdet',
        type: 'select',
        section: 'brandskydd',
        requiredAtFinal: true,
        default: 'landlord',
        options: [
            { value: 'landlord', label: 'Hyresvärden ansvarar vid tillträdet', code: 'landlord', summary: 'Landlord is responsible for fire protection at handover.' },
            { value: 'per_annex', label: 'Ansvar fördelat enligt bilaga', code: 'per_annex', summary: 'Fire-protection responsibility at handover allocated by a separate annex.' },
        ],
    },
    {
        key: 'brandskydd_initial_annex',
        label: 'Bilaga – fördelning av brandskyddsansvar vid tillträde',
        type: 'attachment_ref',
        section: 'brandskydd',
        requiredAtFinal: true,
        attachmentDefaultName: 'Brandskyddsfördelning vid tillträde',
        visibleWhen: { brandskydd_initial_mode: 'per_annex' },
    },
    {
        key: 'brandskydd_maintenance_annex',
        label: 'Bilaga – gränsdragningslista för brandskydd',
        type: 'attachment_ref',
        section: 'brandskydd',
        attachmentDefaultName: 'Gränsdragningslista brandskydd',
        help: 'Valfri — referens till gränsdragningslistan som styr ansvarsfördelningen i övrigt.',
    },
    // §18 Skatter och avgifter (per Fastighetsskatteklausul.docx)
    // Fields are tagged with dotted section paths — editor resolves the
    // path against metadata.sections subClauses to compose the live
    // label prefix (e.g. "22.1", "22.3.a") so labels stay in sync with
    // the document's computed numbering, and renumber when 17.2 hides.
    {
        key: 'property_tax_mode',
        label: 'Fastighetsskatt',
        type: 'select',
        section: 'skatter-avgifter.fastighetsskatt',
        requiredAtFinal: true,
        default: 'included',
        options: [
            { value: 'included', label: 'Fastighetsskatt ingår i hyran', code: 'included', summary: 'Property tax is included in the rent.' },
            {
                value: 'reimbursed_per_annex',
                label: 'Fastighetsskatt ersätts enligt bifogad fastighetsskatteklausul',
                code: 'reimbursed_per_annex',
                summary: 'Property tax reimbursed by the tenant per an annexed property-tax clause.',
            },
        ],
    },
    {
        key: 'property_tax_annex',
        label: 'Bilaga – fastighetsskatteklausul',
        type: 'attachment_ref',
        section: 'skatter-avgifter.fastighetsskatt',
        requiredAtFinal: true,
        attachmentDefaultName: 'Fastighetsskatteklausul',
        visibleWhen: { property_tax_mode: 'reimbursed_per_annex' },
        help: 'Välj eller skapa bilagan som reglerar fastighetsskattens fördelning.',
    },
    {
        key: 'unforeseen_cost_categories',
        label: 'Vilka kategorier omfattas',
        type: 'multi_select',
        section: 'skatter-avgifter.oforutsedda',
        options: [
            {
                value: 'skatt_avgift_palaga',
                label: 'Skatt, avgift eller pålaga (riksdag/regering/myndighet/kommun)',
                code: 'skatt_avgift_palaga',
                summary: 'Tenant shares new taxes, fees or charges imposed by public authorities.',
            },
            {
                value: 'ombyggnadsatgarder',
                label: 'Generella ombyggnadsåtgärder beslutade av offentligt organ',
                code: 'ombyggnadsatgarder',
                summary: 'Tenant shares costs of general rebuilding measures mandated by a public body.',
            },
        ],
        help: 'Markera de kategorier av kostnadsökningar som hyresgästen ska bära andel av. Om ingen markeras utgår klausulen helt.',
    },
    {
        key: 'share_calc_mode',
        label: 'Beräkning av lokalens andel',
        type: 'select',
        section: 'skatter-avgifter.oforutsedda.andelstal',
        default: 'rent_based',
        visibleWhen: { unforeseen_cost_categories: ['ombyggnadsatgarder'] },
        options: [
            { value: 'percent', label: 'Fast procentuell andel av fastighetens totala yta', code: 'percent', summary: 'Share is a fixed percentage of the property total area.' },
            {
                value: 'rent_based',
                label: 'Beräknas på hyresförhållandet (standardregel)',
                code: 'rent_based',
                summary: 'Share computed from the rent relationship (standard rule).',
            },
        ],
    },
    {
        key: 'share_percent',
        label: 'Lokalens andel (%)',
        type: 'text',
        section: 'skatter-avgifter.oforutsedda.andelstal',
        visibleWhen: {
            unforeseen_cost_categories: ['ombyggnadsatgarder'],
            share_calc_mode: 'percent',
        },
        help: 't.ex. 12',
    },
    {
        key: 'tenant_moms_mode',
        label: 'Hyresgästens momsplikt',
        type: 'select',
        section: 'skatter-avgifter.moms.tenant',
        requiredAtFinal: true,
        default: 'momspliktig',
        options: [
            { value: 'momspliktig', label: 'Hyresgästen ska bedriva momspliktig verksamhet', code: 'momspliktig', summary: 'Tenant will conduct VAT-liable business in the premises.' },
            {
                value: 'ej_momspliktig',
                label: 'Hyresgästen ska inte bedriva momspliktig verksamhet',
                code: 'ej_momspliktig',
                summary: 'Tenant will not conduct VAT-liable business in the premises.',
            },
        ],
    },
    {
        key: 'landlord_moms_mode',
        label: 'Hyresvärdens momsplikt',
        type: 'select',
        section: 'skatter-avgifter.moms.landlord',
        requiredAtFinal: true,
        default: 'frivillig_aktiv',
        options: [
            {
                value: 'frivillig_aktiv',
                label: 'Hyresvärden tillämpar frivillig beskattning för moms (aktiv)',
                code: 'frivillig_aktiv',
                summary: 'Landlord actively applies voluntary VAT liability on the rent.',
            },
            {
                value: 'frivillig_vid_val',
                label: 'Om hyresvärden väljer frivillig beskattning ska hyresgästen erlägga moms (villkorad)',
                code: 'frivillig_vid_val',
                summary: 'If the landlord opts for voluntary VAT liability, the tenant must pay VAT (conditional).',
            },
        ],
    },
    // Bilagor section — auto-generated from the contract's bilagor bundle
    // (per ADR-0028 addendum 2026-05-27); no hand-typed list field.
    // §13 Underskrift — no draft-time fields. Ort/datum are captured
    // at signing time (Scrive) or written by hand on the printed PDF
    // (manual delivery).
];
export const sampleValues = {
    landlord_name: 'Fastighetsbolaget Söder AB',
    landlord_org_number: '556789-1234',
    landlord_address: 'Drottninggatan 100',
    landlord_postal_address: '111 60 Stockholm',
    tenant_name: 'Café Söder HB',
    tenant_org_number: '969712-5678',
    tenant_address: 'Götgatan 35',
    tenant_postal_address: '116 21 Stockholm',
    contract_number: 'A-2042',
    object_number: 'L-2042',
    property_designation: 'Söderhuset 14',
    property_address: 'Götgatan 35\n116 21 Stockholm',
    property_kommun: 'Stockholm',
    property_floor: 'BV',
    size_description_mode: 'explicit_area',
    premises_area: 85,
    premises_appurtenances: [
        { name: 'Förråd i källaren', area: 'ca 8 m²' },
        { name: 'Parkeringsplats', area: '1 plats' },
    ],
    include_planlosning: true,
    lease_start_date: '2026-09-01',
    lease_end_date: '2031-08-31',
    notice_months: 9,
    extension_months: 36,
    premises_usage: 'Café- och restaurangverksamhet med servering till allmänheten.',
    premises_condition: 'Befintligt skick med för verksamheten särskilt anpassat skick enligt bilaga',
    inspection_protocol_included: true,
    lock_responsibility: 'Hyresgästen ansvarar',
    maintenance_premises: 'Hyresgästen ansvarar för underhåll av ytskikt och inredning',
    maintenance_common: 'Hyresvärden ansvarar',
    ancillary_spaces_maintenance: true,
    maintenance_ancillary: 'Delat underhållsansvar',
    byggvarudeklaration_included: true,
    sedvanligt_underhall_alt: 'A',
    skyltprogram_included: false,
    brandskydd_initial_mode: 'landlord',
    property_tax_mode: 'included',
    unforeseen_cost_categories: ['skatt_avgift_palaga', 'ombyggnadsatgarder'],
    share_calc_mode: 'rent_based',
    tenant_moms_mode: 'momspliktig',
    landlord_moms_mode: 'frivillig_aktiv',
    rent_amount: 480000,
    payment_frequency: 'kvartalsvis',
    payment_type: 'Enligt avisering i faktura',
    rent_increase_model: 'Indexreglering (KPI, hyrestid ≥ 3 år)',
    rent_base_amount: '480 000 kr',
    // §9.1 Ersättning för el — mätaravläsning är standardmodellen.
    el_mode: 'mataravlasning',
    el_meter_preliminary_yearly: 65000,
    el_meter_andel_percent: '8',
    // §9.2 Ersättning för värme, kyla, drift och skötsel — inverterad
    // per-driftpost-tilldelning (LH-13): varje driftpost i exakt en kategori.
    drift_eget_posts: ['fastighetsel'],
    drift_meter_posts: ['varme', 'va'],
    drift_andel_posts: ['kyla', 'ventilation'],
    drift_bilaga_posts: ['snorojning', 'fastighetsskotsel'],
    drift_andel_percent: '8',
    drift_amount_varme: 24000,
    drift_amount_va: 6000,
    drift_amount_kyla: 8000,
    drift_amount_ventilation: 6000,
    waste_compactor_allowed: true,
    waste_transport_mode: 'landlord_coordinated',
    waste_share_percent: '12',
    waste_yearly_amount: 8400,
    bevakning_mode: 'tenant_own',
    security_type: 'Deposition',
    security_months: 3,
    security_amount: 120000,
    security_account: 'BG 5051-2345',
    possession_protection: 'Hyresgästen har besittningsskydd',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
const MAINTENANCE_PREMISES_OPTIONS = [
    {
        value: 'Hyresvärden ansvarar för underhåll av lokalen',
        heading: 'Hyresvärden ansvarar för underhåll av lokalen',
        body: 'Hyresvärden ska utföra och bekosta erforderligt underhåll av lokalen och inredning som hyresvärden eventuellt särskilt tillhandahåller för verksamheten.',
    },
    {
        value: 'Hyresgästen ansvarar för underhåll av ytskikt och inredning',
        heading: 'Hyresgästen ansvarar för underhåll av ytskikt och inredning',
        body: 'Hyresgästen ska utföra och bekosta erforderligt underhåll dels av ytskikt på golv, väggar och tak, dels av inredning som hyresvärden eventuellt särskilt tillhandahåller för verksamheten.',
    },
    {
        value: 'Annan fördelning enligt bilaga (gränsdragningslista)',
        heading: 'Annan fördelning enligt bilaga (gränsdragningslista)',
        body: 'Annan fördelning av underhållsansvaret framgår av bifogad gränsdragningslista.',
    },
];
const MAINTENANCE_COMMON_OPTIONS = [
    {
        value: 'Hyresvärden ansvarar',
        heading: 'Hyresvärden ansvarar för underhåll av allmänna och gemensamma utrymmen',
        body: 'Om inte annat överenskommits mellan parterna ansvarar hyresvärden för underhåll av allmänna och gemensamma utrymmen.',
    },
    {
        value: 'Annan överenskommelse enligt bilaga',
        heading: 'Annan överenskommelse enligt bilaga',
    },
];
/**
 * §14.5.b sedvanligt underhåll alternatives. Verbatim from
 * "Ombyggnads- och ändringsarbeten + skyltning.docx".
 */
const SEDVANLIGT_UNDERHALL_OPTIONS = [
    {
        value: 'A',
        heading: 'Alternativ A',
        body: 'Hyresgästen har inte rätt till nedsättning av hyran för hinder eller men i nyttjanderätten till följd av att Hyresvärden låter utföra sedvanligt underhåll av Lokalen eller Fastigheten i övrigt. Det åligger dock Hyresvärden att i god tid underrätta Hyresgästen om arbetets art och omfattning samt när och under vilken tid arbetet ska utföras.',
    },
    {
        value: 'B',
        heading: 'Alternativ B',
        body: 'Nedsättning av hyran för hinder eller men i nyttjanderätten till följd av att Hyresvärden låter utföra sedvanligt underhåll av Lokalen eller Fastigheten i övrigt utgår enligt hyreslagens regler.',
    },
    {
        value: 'C',
        heading: 'Alternativ C',
        body: 'Parterna är överens om att rätten till nedsättning av hyran när Hyresvärden låter utföra sedvanligt underhåll av Lokalen eller Fastigheten i övrigt ska regleras enligt särskild bilaga.',
    },
];
const MAINTENANCE_ANCILLARY_OPTIONS = [
    {
        value: 'Hyresvärden ansvarar för underhåll av biutrymmen',
        heading: 'Hyresvärden ansvarar för underhåll av biutrymmen',
        body: 'Hyresvärden ska utföra och bekosta erforderligt underhåll av biutrymmen som ingår i hyresobjektet. Hyresgästen ska hålla biutrymmena i städat och välvårdat skick samt utan dröjsmål anmäla skador och brister till hyresvärden.',
    },
    {
        value: 'Hyresgästen ansvarar för underhåll av biutrymmen',
        heading: 'Hyresgästen ansvarar för underhåll av biutrymmen',
        body: 'Hyresgästen ska utföra och bekosta erforderligt underhåll av biutrymmen som ingår i hyresobjektet, innefattande städning, enklare reparationer samt underhåll av ytskikt. Hyresgästen är skyldig att hålla biutrymmena i sådant skick att de vid hyresförhållandets upphörande kan återlämnas i godtagbart skick.',
    },
    {
        value: 'Delat underhållsansvar',
        heading: 'Delat underhållsansvar',
        body: 'Hyresvärden ansvarar för underhåll av biutrymmenas stomme, tak och fasta installationer. Hyresgästen ansvarar för underhåll av ytskikt på golv och väggar samt för städning och ordning i biutrymmena.',
    },
    {
        value: 'Annan fördelning enligt bilaga (gränsdragningslista)',
        heading: 'Annan fördelning enligt bilaga (gränsdragningslista)',
        body: 'Annan fördelning av underhållsansvaret för biutrymmen framgår av bifogad gränsdragningslista.',
    },
];
function SubClauseHeading({ children }) {
    return (_jsx("p", { className: "mt-3 mb-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.08em] text-stone-600", children: children }));
}
/**
 * Single ☑/☐ row used inline within a clause. Keeps the verbatim text
 * next to its checkbox, with the box marking the contract's selection.
 */
function ClauseCheck({ checked, children, }) {
    return (_jsxs("div", { className: "flex items-start gap-1.5 text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "mt-0.5 select-none", children: checked ? '☑' : '☐' }), _jsx("div", { className: "flex-1", children: children })] }));
}
/**
 * §9 (Taxebundna tillägg) alternative renderer — used in place of
 * ClauseCheck for the el / drift sub-clauses. Per UX feedback
 * 2026-05-28 the all-options-with-☑/☐ layout was too cluttered in the
 * rendered contract. In blank mode every option still renders (so the
 * gallery / authoring view stays informative); in preview/final only
 * the active option renders, with no checkbox marker. Scoped to §9 —
 * the §13 / §14 maintenance clauses keep the checkbox-list pattern.
 */
function TaxeOption({ active, mode, children, }) {
    if (mode !== 'blank' && !active)
        return null;
    return (_jsx("div", { className: "text-[10.5px] leading-relaxed text-stone-700", children: children }));
}
function ClauseChoice({ options, selected, annexValue, annexLabel, }) {
    return (_jsx("div", { className: "flex flex-col gap-1.5", children: options.map((opt) => {
            const checked = selected === opt.value;
            const suffix = checked && annexValue === opt.value && annexLabel
                ? ` (${annexLabel})`
                : '';
            return (_jsxs("div", { className: "text-[10.5px] leading-relaxed", children: [_jsx("span", { className: "mr-1.5", children: checked ? '☑' : '☐' }), _jsxs("span", { className: "font-semibold", children: [opt.heading, opt.body ? '' : suffix] }), opt.body ? (_jsxs("p", { className: "m-0 mt-0.5 whitespace-pre-wrap pl-[1.5em] text-stone-700", children: [opt.body, suffix] })) : null] }, opt.value));
        }) }));
}
/**
 * Inline "fill-in" slot used inside clause prose — renders the value with
 * a thin underline, or a blank line when the value is missing. Mirrors
 * the visual treatment used by FieldRow.
 */
function InlineSlot({ value, fallback = '____', suffix, }) {
    const display = (() => {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return num(value);
        }
        if (typeof value === 'string' && value.trim().length > 0) {
            return value;
        }
        return null;
    })();
    return (_jsxs("span", { className: "border-b border-stone-400 px-1 font-serif", children: [display ?? fallback, suffix && display ? ` ${suffix}` : suffix ? ` ${suffix}` : ''] }));
}
/**
 * §9.2 driftposter table. Renders one row per post with a non-zero
 * amount; in `blank` mode shows every post with a blank cell so the
 * author sees the full possible shape. Caption text comes from the
 * caller so the same table renders under mätaravläsning / andelstal /
 * fast tillägg with the right column heading.
 */
function DriftTable({ values, mode, caption, postKeys, }) {
    const basePosts = postKeys
        ? DRIFT_TABLE_POSTS.filter((p) => postKeys.includes(p.key))
        : DRIFT_TABLE_POSTS;
    const rows = basePosts.map((post) => {
        const amount = values[`drift_amount_${post.key}`];
        const numeric = typeof amount === 'number' && Number.isFinite(amount) ? amount : 0;
        return { ...post, amount: numeric };
    });
    const visibleRows = mode === 'blank' ? rows : rows.filter((r) => r.amount > 0);
    const total = rows.reduce((sum, r) => sum + r.amount, 0);
    if (visibleRows.length === 0 && mode !== 'blank') {
        return (_jsx("p", { className: "m-0 mt-2 text-[10px] italic text-stone-500", children: "(Inga driftposter angivna.)" }));
    }
    return (_jsxs("table", { className: "mt-2 w-full border-collapse text-[10px]", children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { className: "border-b border-stone-300 py-1 pr-2 text-left font-sans font-semibold uppercase tracking-[0.06em] text-stone-600", children: "Driftkostnad" }), _jsx("th", { className: "border-b border-stone-300 py-1 pl-2 text-right font-sans font-semibold uppercase tracking-[0.06em] text-stone-600", children: caption })] }) }), _jsxs("tbody", { children: [visibleRows.map((row) => (_jsxs("tr", { children: [_jsx("td", { className: "py-0.5 pr-2 text-stone-700", children: row.label }), _jsx("td", { className: "py-0.5 pl-2 text-right font-serif text-stone-700", children: row.amount > 0 ? `${num(row.amount)} kr/år` : '____ kr/år' })] }, row.key))), _jsxs("tr", { children: [_jsx("td", { className: "border-t border-stone-300 py-1 pr-2 font-semibold text-stone-800", children: "Totalt" }), _jsx("td", { className: "border-t border-stone-300 py-1 pl-2 text-right font-serif font-semibold text-stone-800", children: total > 0 ? `${num(total)} kr/år` : '____ kr/år' })] })] })] }));
}
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, attachments, signatories, }) {
    const hasCotenant = cond(mode, !!(values.cotenant_name ||
        values.cotenant_org_number ||
        values.cotenant_address ||
        values.cotenant_postal_address));
    const hasGuarantor = cond(mode, !!values.guarantor_name);
    const ancillaryShown = cond(mode, !!values.ancillary_spaces_maintenance);
    const enteredAppurtenances = Array.isArray(values.premises_appurtenances)
        ? values.premises_appurtenances
        : [];
    const appurtenanceRows = enteredAppurtenances.length === 0 && mode === 'blank'
        ? [{ name: '', area: '' }]
        : enteredAppurtenances;
    const hasAppurtenances = cond(mode, enteredAppurtenances.length > 0);
    // Default the appurtenance area unit to kvm when the entered value is a
    // bare number with no unit; preserve any unit the author typed.
    const appurtenanceArea = (raw) => {
        const text = v(raw);
        if (text === PLACEHOLDER)
            return text;
        return /^[\d\s.,]+$/.test(text) ? `${text} kvm` : text;
    };
    // Fast höjning: drop the parentheses wrapping the time/period in the
    // displayed model value, keeping the words. The stored value (used as a
    // visibleWhen/body discriminant) is left untouched.
    const rentIncreaseModelLabel = values.rent_increase_model === 'Fast höjning (hyrestid 1–3 år)'
        ? 'Fast höjning hyrestid 1–3 år'
        : values.rent_increase_model;
    // §8 KPI basår (LH-10): the standard KPI series basår is 2020. When the
    // author supplies a non-standard basår via `rent_index_base_year`, the
    // whole index clause adapts — the inline "totalindex med <år> som basår"
    // and the trailing 1980→<år> conversion paragraph both follow the
    // supplied year. Verbatim KPI wording is otherwise untouched.
    const INDEX_STANDARD_BASE_YEAR = '2020';
    const indexBaseYear = typeof values.rent_index_base_year === 'string' &&
        values.rent_index_base_year.trim().length > 0
        ? values.rent_index_base_year.trim()
        : INDEX_STANDARD_BASE_YEAR;
    // §9.2 (LH-13): resolve per-media-type category assignment with
    // precedence dedup — a media type lands in exactly one category even if
    // the editor failed to enforce exclusivity (eget avtal > mätaravläsning >
    // andelstal > endast bilaga). Cross-field exclusivity is the editor's
    // job (NEEDS ANDRÉ VALIDATION); this is a render-time guard.
    const arr = (raw) => Array.isArray(raw) ? raw.filter((x) => typeof x === 'string') : [];
    const driftAssigned = new Set();
    const takePosts = (raw) => {
        const out = [];
        for (const key of arr(raw)) {
            if (driftAssigned.has(key))
                continue;
            driftAssigned.add(key);
            out.push(key);
        }
        return out;
    };
    const driftEget = takePosts(values.drift_eget_posts);
    const driftMeter = takePosts(values.drift_meter_posts);
    const driftAndel = takePosts(values.drift_andel_posts);
    const driftBilaga = takePosts(values.drift_bilaga_posts);
    const driftHasMeter = cond(mode, driftMeter.length > 0);
    const driftHasAndel = cond(mode, driftAndel.length > 0);
    const driftHasEget = cond(mode, driftEget.length > 0);
    const driftHasBilaga = cond(mode, driftBilaga.length > 0);
    // Inline the picked bilaga reference into the skick option text:
    // "...enligt bilaga" → "...enligt Bilaga 2 och Bilaga 3".
    // Resolve a dotted section path to its full numeric ref ("9.1.a") at
    // the current values+mode. Used to compose FieldRow labels for sections
    // whose sub-clauses don't use the SubClause atom (e.g. §9, which uses
    // compact label/value rows instead of prose blocks).
    const subRef = (p) => resolveSectionPath(metadata.sections ?? [], p, values, mode) ?? '';
    const premisesConditionText = (() => {
        const value = values.premises_condition;
        if (typeof value !== 'string' || value.length === 0)
            return v(value);
        if (!hasAttachmentRef(values.condition_annex))
            return value;
        return value.replace(/enligt bilaga$/i, `enligt ${attachmentLabels(attachments, values.condition_annex)}`);
    })();
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsxs("div", { className: "mb-2 flex flex-wrap gap-x-8 gap-y-1 text-[10px] text-stone-700", children: [_jsxs("div", { children: [_jsx("span", { className: "font-sans uppercase tracking-[0.1em] text-stone-500", children: "Avtalsnummer:" }), ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.contract_number) })] }), cond(mode, !!values.object_number) && (_jsxs("div", { children: [_jsx("span", { className: "font-sans uppercase tracking-[0.1em] text-stone-500", children: "Objektsnummer:" }), ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.object_number) })] }))] }), _jsx("p", { className: "mb-4 text-[10px] italic text-stone-600", children: metadata.preamble }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsxs(Section, { sectionRef: "hyresvard", title: "Hyresv\u00E4rd", children: [_jsx(FieldRow, { label: "Namn", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Org.nr", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Gatuadress", children: v(values.landlord_address) }), _jsx(FieldRow, { label: "Postnummer", children: v(values.landlord_postal_address) })] }), _jsxs(Section, { sectionRef: "hyresgast", title: "Hyresg\u00E4st", children: [_jsx(FieldRow, { label: "Namn", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Org.nr", children: v(values.tenant_org_number) }), _jsx(FieldRow, { label: "Gatuadress", children: v(values.tenant_address) }), _jsx(FieldRow, { label: "Postnummer", children: v(values.tenant_postal_address) }), hasCotenant && (_jsxs(_Fragment, { children: [_jsx("div", { className: "mt-2 mb-1 font-sans text-[10px] uppercase tracking-[0.1em] text-stone-500", children: "Medhyresg\u00E4st" }), _jsx(FieldRow, { label: "Namn", children: v(values.cotenant_name) }), _jsx(FieldRow, { label: "Org.nr", children: v(values.cotenant_org_number) }), _jsx(FieldRow, { label: "Gatuadress", children: v(values.cotenant_address) }), _jsx(FieldRow, { label: "Postnummer", children: v(values.cotenant_postal_address) })] }))] })] }), _jsxs(Section, { sectionRef: "hyresobjekt", title: "Hyresobjekt", children: [_jsx(FieldRow, { label: "Fastighetsbeteckning", children: v(values.property_designation) }), _jsx(FieldRow, { label: "Lokalens adress", children: v(values.property_address) }), _jsx(FieldRow, { label: "Kommun", children: v(values.property_kommun) }), _jsx(FieldRow, { label: "V\u00E5ningsplan", children: v(values.property_floor) }), (mode === 'blank' ||
                        values.size_description_mode !== 'per_drawing' ||
                        typeof values.premises_area === 'number') && (_jsx(FieldRow, { label: "Yta (m\u00B2)", children: num(values.premises_area) })), cond(mode, values.size_description_mode === 'per_drawing') && (_jsxs("p", { className: "mt-1 text-[10px] leading-relaxed text-stone-700", children: ["Lokalens storlek och omfattning framg\u00E5r av ovan angivna uppgifter och bilagd(a) handling(-ar).", ' ', hasAttachmentRef(values.size_drawing_annex)
                                ? attachmentLabels(attachments, values.size_drawing_annex)
                                : 'Bilaga ____', "."] })), hasAppurtenances && (_jsxs("div", { className: "mt-2", children: [_jsx("div", { className: "mb-1 font-sans text-[10px] uppercase tracking-[0.1em] text-stone-500", children: "Tillh\u00F6rande utrymmen" }), _jsxs("div", { className: "flex flex-col text-[10px] text-stone-700", children: [_jsxs("div", { className: "grid grid-cols-[3fr_1fr] gap-2 border-b border-stone-300 py-0.5 font-sans text-[9px] uppercase tracking-[0.08em] text-stone-500", children: [_jsx("span", { children: "Utrymme" }), _jsx("span", { children: "Yta" })] }), appurtenanceRows.map((row, i) => (_jsxs("div", { className: "grid grid-cols-[3fr_1fr] gap-2 border-b border-stone-200 py-0.5", children: [_jsx("span", { children: v(row.name) }), _jsx("span", { className: "font-serif", children: appurtenanceArea(row.area) })] }, i)))] })] })), _jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "Areaavvikelse." }), " Om i avtalet angiven area avviker fr\u00E5n faktisk area medf\u00F6r avvikelsen inte r\u00E4tt f\u00F6r Hyresg\u00E4sten till \u00E5terbetalning eller s\u00E4nkning av hyran respektive Hyresv\u00E4rden r\u00E4tt till h\u00F6gre hyra."] })] }), _jsxs(Section, { sectionRef: "hyrestid-uppsagning", title: "Hyrestid och upps\u00E4gning", children: [_jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(FieldRow, { label: "Startdatum", children: v(values.lease_start_date) }), _jsx(FieldRow, { label: "Slutdatum", children: v(values.lease_end_date) }), _jsx(FieldRow, { label: "\u00D6msesidig upps\u00E4gningstid (m\u00E5nader)", children: num(values.notice_months) }), _jsx(FieldRow, { label: "F\u00F6rl\u00E4ngning om ej uppsagt (m\u00E5nader)", children: num(values.extension_months) })] }), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Upps\u00E4gning av avtalet ska vara skriftlig. Parterna ska iaktta \u00F6vriga formkrav som f\u00F6ljer av 12 kap. jordabalken." })] }), _jsx(Section, { sectionRef: "anvandning", title: "Lokalens anv\u00E4ndning", children: _jsx("p", { className: "text-[10px] text-stone-700 whitespace-pre-wrap", children: v(values.premises_usage) }) }), _jsxs(Section, { sectionRef: "skick", title: "Lokalens skick", children: [_jsx(FieldRow, { label: "Lokalen hyrs ut i", children: premisesConditionText }), cond(mode, !!values.inspection_protocol_included) && (_jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["En beskrivning av lokalens skick vid avtalets ing\u00E5ende samt uppgift om vem som p\u00E5 tilltr\u00E4desdagen i f\u00F6rekommande fall ska ha ombes\u00F6rjt respektive bekostat \u00E5tg\u00E4rdandet av brister eller \u00F6verenskomna \u00E4ndringar av lokalen framg\u00E5r av bifogat besiktnings- och \u00E5tg\u00E4rdsprotokoll", hasAttachmentRef(values.inspection_protocol_annex)
                                ? ` (${attachmentLabel(attachments, values.inspection_protocol_annex)})`
                                : '', "."] }))] }), _jsxs(Section, { sectionRef: "hyra", title: "Hyra", children: [_jsxs("p", { className: "mb-2 text-[10px] leading-relaxed text-stone-700", children: ["\u00C5rshyran uppg\u00E5r till", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: typeof values.rent_amount === 'number'
                                    ? `${num(values.rent_amount)} kr`
                                    : '               ' }), "."] }), _jsxs("p", { className: "mb-2 text-[10px] leading-relaxed text-stone-700", children: ["Hyran aviseras", ' ', _jsx("span", { className: "font-semibold", children: v(values.payment_frequency) }), ' ', "och ska betalas utan anfordran i f\u00F6rskott senast sista vardagen f\u00F6re varje", ' ', values.payment_frequency === 'månadsvis'
                                ? 'kalendermånads'
                                : values.payment_frequency === 'årsvis'
                                    ? 'kalenderårs'
                                    : 'kalenderkvartals', ' ', "b\u00F6rjan."] }), _jsxs("div", { className: "flex flex-col gap-1 text-[10px] text-stone-700", children: [_jsxs("div", { children: [_jsx("span", { className: "mr-1", children: values.payment_type === 'Enligt avisering i faktura' ? '☑' : '☐' }), "Hyran betalas enligt avisering."] }), _jsxs("div", { children: [_jsx("span", { className: "mr-1", children: values.payment_type === 'Plusgiro' || values.payment_type === 'Bankgiro' ? '☑' : '☐' }), "Hyran betalas genom ins\u00E4ttning p\u00E5 Hyresv\u00E4rdens konto:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.payment_account) })] })] }), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Vid f\u00F6rsenad hyresbetalning ska hyresg\u00E4sten betala dr\u00F6jsm\u00E5lsr\u00E4nta och ers\u00E4ttning f\u00F6r betalningsp\u00E5minnelse, inkasso och dylikt enligt lag." })] }), _jsxs(Section, { sectionRef: "arlig-hojning", title: "Hyrans \u00E5rsvisa h\u00F6jning", children: [_jsx(FieldRow, { label: "Modell", children: v(rentIncreaseModelLabel) }), cond(mode, values.rent_increase_model === 'Fast höjning (hyrestid 1–3 år)') && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Fast h\u00F6jningsvillkor", children: v(values.rent_fixed_increase) }), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Om hyrestiden enligt p. 4 \u00E4r l\u00E4ngre \u00E4n 1 \u00E5r men kortare \u00E4n tre \u00E5r, ska hyran enligt p. 7 h\u00F6jas enligt ovan angivna villkor." })] })), cond(mode, values.rent_increase_model === 'Indexreglering (KPI, hyrestid ≥ 3 år)' &&
                        !!values.rent_index_manual) && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Bashyra (kr eller %)", children: v(values.rent_base_amount) }), _jsx("p", { className: "mt-2 whitespace-pre-wrap text-[10px] leading-relaxed text-stone-700", children: v(values.rent_index_manual_text) })] })), cond(mode, values.rent_increase_model === 'Indexreglering (KPI, hyrestid ≥ 3 år)' &&
                        !values.rent_index_manual) && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Bashyra (kr eller %)", children: v(values.rent_base_amount) }), cond(mode, !!values.rent_index_base_year) && (_jsx(FieldRow, { label: "Avvikande bastal", children: v(values.rent_index_base_year) })), _jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["Om hyrestiden enligt p. 4 \u00E4r minst tre \u00E5r, ska hyran enligt p. 7 indexeras \u00E5rligen. Av det i kontraktet angivna hyresbeloppet ska", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.rent_base_amount) }), ' ', "utg\u00F6ra bashyra. Under hyrestiden ska med h\u00E4nsyn till f\u00F6r\u00E4ndringarna i konsumentprisindex (totalindex med ", indexBaseYear, " som bas\u00E5r) till\u00E4gg till hyresbeloppet utg\u00E5 med en viss procent av bashyran enligt nedanst\u00E5ende grunder."] }), _jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["F\u00F6r hyresavtal som b\u00F6rjar l\u00F6pa n\u00E5gon g\u00E5ng under tiden 1/1\u201330/6 anses bashyran anpassad till indextalet f\u00F6r oktober m\u00E5nad \u00E5ret innan. F\u00F6r hyresavtal som b\u00F6rjar l\u00F6pa n\u00E5gon g\u00E5ng under tiden 1/7\u201331/12 anses bashyran i st\u00E4llet anpassad till indextalet f\u00F6r oktober m\u00E5nad under samma tid. Indextalet f\u00F6r den oktober m\u00E5nad till vilken bashyran enligt ovan anses anpassad utg\u00F6r bastal s\u00E5vida inte annat avtalats", values.rent_index_base_year ? (_jsxs(_Fragment, { children: [": indextalet f\u00F6r oktober m\u00E5nad \u00E5r", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.rent_index_base_year) })] })) : null, "."] }), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Skulle indextalet n\u00E5gon p\u00E5f\u00F6ljande oktober m\u00E5nad ha stigit i f\u00F6rh\u00E5llande till bastalet, ska till\u00E4gg utg\u00E5 med det procenttal varmed indextalet \u00E4ndrats i f\u00F6rh\u00E5llande till bastalet. I forts\u00E4ttningen ska till\u00E4gg utg\u00E5 i f\u00F6rh\u00E5llande till index\u00E4ndringarna, varvid hyresf\u00F6r\u00E4ndringen ber\u00E4knas p\u00E5 basis av den procentuella f\u00F6r\u00E4ndringen mellan bastalet och indextalet f\u00F6r respektive oktober m\u00E5nad. Till\u00E4gget kan bli s\u00E5v\u00E4l h\u00F6gre som l\u00E4gre mellan olika \u00E5r. Utg\u00E5ende hyra ska dock aldrig s\u00E4ttas l\u00E4gre \u00E4n det i kontraktet angivna hyresbeloppet. Hyres\u00E4ndringen sker alltid fr.o.m. den 1 januari efter det att oktoberindex f\u00F6ranlett omr\u00E4kning." }), cond(mode, !values.rent_index_base_year) && (_jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["Om bastalet utg\u00F6rs av indextal f\u00F6r m\u00E5nad med fastst\u00E4llt indextal med bas\u00E5r 1980 (oktober m\u00E5nad t.o.m. \u00E5r 2025) ska indextalet omr\u00E4knas till index med bas\u00E5r ", indexBaseYear, "genom att bastalet divideras med medeltalet av KPI f\u00F6r ", indexBaseYear, " (335,92 med bas\u00E5r 1980) och kvoten multipliceras med 100, avrundat till tv\u00E5 decimaler."] }))] })), cond(mode, values.rent_increase_model === 'Indexreglering enligt bilaga') && (_jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["Indexreglering av hyran sker enligt s\u00E4rskild bilaga.", ' ', hasAttachmentRef(values.rent_index_annex)
                                ? attachmentLabel(attachments, values.rent_index_annex)
                                : 'Bilaga ____', "."] }))] }), _jsx(Section, { sectionRef: "forbrukning", title: "Taxebundna till\u00E4gg: El, v\u00E4rme, kyla och drift", children: _jsxs(SubClauseProvider, { entries: [
                        { id: 'el', visible: true },
                        { id: 'drift', visible: true },
                    ], children: [_jsx(SubClause, { id: "el", title: "Ers\u00E4ttning f\u00F6r el", children: _jsxs("div", { className: "flex flex-col gap-2", children: [_jsxs(TaxeOption, { active: values.el_mode === 'eget_avtal', mode: mode, children: [_jsx("span", { className: "font-semibold", children: "Eget avtal." }), ' ', "Hyresg\u00E4sten tecknar eget elavtal direkt med elleverant\u00F6r och n\u00E4t\u00E4gare. Hyresv\u00E4rden ansvarar inte f\u00F6r elleverans till Lokalen."] }), _jsxs(TaxeOption, { active: values.el_mode === 'mataravlasning', mode: mode, children: [_jsx("span", { className: "font-semibold", children: "M\u00E4taravl\u00E4sning." }), ' ', "Hyresg\u00E4sten ska genom ett \u00E5rligt prelimin\u00E4rt hyrestill\u00E4gg betala f\u00F6r sin uppm\u00E4tta f\u00F6rbrukning av el (elleverans och n\u00E4tabonnemang) som tillhandah\u00E5lls av Hyresv\u00E4rden. Prelimin\u00E4r ers\u00E4ttning utg\u00E5r med", ' ', _jsx(InlineSlot, { value: values.el_meter_preliminary_yearly }), " kronor per \u00E5r.", '\n\n', "Den prelimin\u00E4ra ers\u00E4ttningen avr\u00E4knas kalender\u00E5rsvis i efterskott, senast den 30 juni f\u00F6r f\u00F6reg\u00E5ende kalender\u00E5r. Upph\u00F6r hyresavtalet vid annan tidpunkt \u00E4n \u00E5rsskifte sker avr\u00E4kning mot faktisk f\u00F6rbrukning i samband med avtalets upph\u00F6rande, eller s\u00E5 snart avl\u00E4sning av m\u00E4tare kan ske.", '\n\n', "F\u00F6r det fall Hyresg\u00E4stens enskilda f\u00F6rbrukning inte kan m\u00E4tas med underm\u00E4tare ska Hyresg\u00E4stens andel av de totala kostnaderna motsvara ytan av den f\u00F6rhyrda Lokalen i f\u00F6rh\u00E5llande till Fastighetens totala uthyrbara yta.", '\n\n', "Vid avtalets undertecknande uppg\u00E5r Hyresg\u00E4stens andel till", ' ', _jsx(InlineSlot, { value: values.el_meter_andel_percent }), " %.", '\n\n', "Hyresg\u00E4sten har inte r\u00E4tt till r\u00E4nta p\u00E5 prelimin\u00E4rdebiterat belopp."] }), _jsxs(TaxeOption, { active: values.el_mode === 'andelstal', mode: mode, children: [_jsx("span", { className: "font-semibold", children: "Andelstal." }), ' ', "Hyresg\u00E4sten ska genom ett \u00E5rligt prelimin\u00E4rt hyrestill\u00E4gg betala f\u00F6r sin andel av Fastighetens totala kostnad f\u00F6r el (elleverans och n\u00E4tabonnemang) som tillhandah\u00E5lls av Hyresv\u00E4rden.", '\n\n', "Hyresg\u00E4stens andel av de totala kostnaderna motsvarar ytan av den f\u00F6rhyrda Lokalen i f\u00F6rh\u00E5llande till Fastighetens totala uthyrbara yta.", '\n\n', "Vid avtalets undertecknande uppg\u00E5r Hyresg\u00E4stens andel till", ' ', _jsx(InlineSlot, { value: values.el_andelstal_percent }), " %, motsvarande ett hyrestill\u00E4gg om", ' ', _jsx(InlineSlot, { value: values.el_andelstal_yearly }), " kronor per \u00E5r.", '\n\n', "Det prelimin\u00E4ra hyrestill\u00E4gget avr\u00E4knas kalender\u00E5rsvis i efterskott, senast den 30 juni f\u00F6r f\u00F6reg\u00E5ende kalender\u00E5r.", '\n\n', "Hyresg\u00E4sten har inte r\u00E4tt till r\u00E4nta p\u00E5 prelimin\u00E4rdebiterat belopp."] }), _jsxs(TaxeOption, { active: values.el_mode === 'fast_uppraknings', mode: mode, children: [_jsx("span", { className: "font-semibold", children: "Fast till\u00E4gg med \u00E5rlig uppr\u00E4kning." }), ' ', "Hyresg\u00E4sten ska genom ett fast hyrestill\u00E4gg betala ers\u00E4ttning f\u00F6r el. Hyrestill\u00E4gget uppg\u00E5r vid avtalets ing\u00E5ende till", ' ', _jsx(InlineSlot, { value: values.el_fast_yearly }), " kronor per \u00E5r.", '\n\n', "Hyrestill\u00E4gget ska \u00E5rligen r\u00E4knas upp enligt f\u00F6ljande:", _jsxs("div", { className: "mt-1.5 flex flex-col gap-1", children: [_jsxs(TaxeOption, { active: values.el_mode === 'fast_uppraknings' &&
                                                            values.el_fast_uppraknings_modell === 'fast_procent', mode: mode, children: [_jsx("span", { className: "font-semibold", children: "Fast procentsats" }), " \u2014 med en procentsats om", ' ', _jsx(InlineSlot, { value: values.el_fast_procent_per_year }), " % per \u00E5r."] }), _jsxs(TaxeOption, { active: values.el_mode === 'fast_uppraknings' &&
                                                            values.el_fast_uppraknings_modell === 'kpi', mode: mode, children: [_jsx("span", { className: "font-semibold", children: "KPI" }), " \u2014 i proportion till f\u00F6r\u00E4ndringen i konsumentprisindex (KPI), med", ' ', _jsx(InlineSlot, { value: values.el_fast_kpi_base_month_year }), " som bastal och", ' ', _jsx(InlineSlot, { value: values.el_fast_kpi_comparison_month }), ' ', "m\u00E5nad n\u00E4rmast f\u00F6re uppr\u00E4kningstidpunkten som j\u00E4mf\u00F6relsetal. Uppr\u00E4kning sker per den 1 januari. Hyrestill\u00E4gget ska aldrig understiga f\u00F6reg\u00E5ende \u00E5rs niv\u00E5."] }), _jsxs(TaxeOption, { active: values.el_mode === 'fast_uppraknings' &&
                                                            values.el_fast_uppraknings_modell === 'faktisk_kostnad', mode: mode, children: [_jsx("span", { className: "font-semibold", children: "Faktisk kostnad" }), " \u2014 i enlighet med Hyresv\u00E4rdens faktiska kostnadsutveckling f\u00F6r de driftposter som hyrestill\u00E4gget avser. Hyresv\u00E4rden ska senast den ", _jsx(InlineSlot, { value: values.el_fast_faktisk_notice_date }), ' ', "varje \u00E5r skriftligen meddela Hyresg\u00E4sten det justerade hyrestill\u00E4gget, med uppgift om underliggande kostnadsf\u00F6r\u00E4ndringar. Justering sker per den 1 januari med fakturering retroaktivt f\u00F6r perioden januari till och med aviseringsdatumet."] })] })] })] }) }), _jsxs(SubClause, { id: "drift", title: "Ers\u00E4ttning f\u00F6r v\u00E4rme, kyla, drift och sk\u00F6tsel m.m.", children: [_jsx("p", { className: "m-0 text-[10.5px] leading-relaxed text-stone-700", children: "T\u00E4cker v\u00E4rme, VA, kyla, sn\u00F6r\u00F6jning, ventilation, fastighetsel och fastighetssk\u00F6tsel. Respektive driftpost regleras enligt det alternativ som anges nedan." }), _jsxs("div", { className: "mt-2 flex flex-col gap-3", children: [driftHasEget && (_jsxs("div", { className: "text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "Eget avtal." }), ' ', "Hyresg\u00E4sten tecknar eget avtal direkt f\u00F6r f\u00F6ljande driftposter:", ' ', driftEget.length > 0
                                                    ? driftEget.map(driftMediaLabel).join(', ')
                                                    : '____', "."] })), driftHasMeter && (_jsxs("div", { className: "text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "M\u00E4taravl\u00E4sning." }), ' ', "Hyresg\u00E4sten ska genom ett \u00E5rligt prelimin\u00E4rt hyrestill\u00E4gg betala f\u00F6r sin uppm\u00E4tta andel av Fastighetens totala kostnad f\u00F6r nedanst\u00E5ende driftposter som tillhandah\u00E5lls av Hyresv\u00E4rden.", '\n\n', "Kostnader som omfattas av hyrestill\u00E4gget specificeras i tabellen nedan:", _jsx(DriftTable, { values: values, mode: mode, postKeys: driftMeter, caption: "Prelimin\u00E4r debitering vid avtalets ing\u00E5ende (kr/\u00E5r)" }), _jsxs("p", { className: "m-0 mt-2 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: ["Den prelimin\u00E4ra ers\u00E4ttningen avr\u00E4knas kalender\u00E5rsvis i efterskott, senast den 30 juni f\u00F6r f\u00F6reg\u00E5ende kalender\u00E5r. Upph\u00F6r hyresavtalet vid annan tidpunkt \u00E4n \u00E5rsskifte sker avr\u00E4kning mot faktisk f\u00F6rbrukning i samband med avtalets upph\u00F6rande, eller s\u00E5 snart avl\u00E4sning av m\u00E4tare kan ske.", '\n\n', "F\u00F6r det fall Hyresg\u00E4stens enskilda f\u00F6rbrukning inte kan m\u00E4tas med underm\u00E4tare ska Hyresg\u00E4stens andel av de totala kostnaderna motsvara ytan av den f\u00F6rhyrda Lokalen i f\u00F6rh\u00E5llande till Fastighetens totala uthyrbara yta.", '\n\n', "Vid avtalets undertecknande uppg\u00E5r Hyresg\u00E4stens andel till", ' ', _jsx(InlineSlot, { value: values.drift_andel_percent }), " %.", '\n\n', "Hyresg\u00E4sten har inte r\u00E4tt till r\u00E4nta p\u00E5 prelimin\u00E4rdebiterat belopp."] })] })), driftHasAndel && (_jsxs("div", { className: "text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "Andelstal." }), ' ', "Hyresg\u00E4sten ska genom ett \u00E5rligt prelimin\u00E4rt hyrestill\u00E4gg betala f\u00F6r sin andel av Fastighetens totala kostnad f\u00F6r nedanst\u00E5ende driftposter som tillhandah\u00E5lls av Hyresv\u00E4rden.", '\n\n', "Hyresg\u00E4stens andel av de totala kostnaderna motsvarar ytan av den f\u00F6rhyrda Lokalen i f\u00F6rh\u00E5llande till Fastighetens totala uthyrbara yta.", '\n\n', "Kostnader som omfattas av hyrestill\u00E4gget specificeras i tabellen nedan:", _jsx(DriftTable, { values: values, mode: mode, postKeys: driftAndel, caption: "Prelimin\u00E4r debitering vid avtalets ing\u00E5ende (kr/\u00E5r)" }), _jsxs("p", { className: "m-0 mt-2 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: ["Vid avtalets undertecknande uppg\u00E5r Hyresg\u00E4stens andel till", ' ', _jsx(InlineSlot, { value: values.drift_andel_percent }), " %.", '\n\n', "Det prelimin\u00E4ra hyrestill\u00E4gget avr\u00E4knas kalender\u00E5rsvis i efterskott, senast den 30 juni f\u00F6r f\u00F6reg\u00E5ende kalender\u00E5r.", '\n\n', "Hyresg\u00E4sten har inte r\u00E4tt till r\u00E4nta p\u00E5 prelimin\u00E4rdebiterat belopp."] })] })), driftHasBilaga && (_jsxs("div", { className: "text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "Reglering enligt driftskostnadsklausul (bilaga)." }), ' ', "F\u00F6ljande driftposter regleras i den bifogade driftskostnadsklausulen:", ' ', driftBilaga.length > 0
                                                    ? driftBilaga.map(driftMediaLabel).join(', ')
                                                    : '____', ".", ' ', hasAttachmentRef(values.drift_annex)
                                                    ? attachmentLabel(attachments, values.drift_annex)
                                                    : 'Bilaga ____', "."] }))] }), cond(mode, hasAttachmentRef(values.drift_annex)) && (_jsxs("p", { className: "mt-3 m-0 text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "\u00D6vrig driftkostnad. " }), "\u00D6vriga driftkostnader regleras i en separat driftskostnadsklausul som g\u00E4ller som bilaga till detta avtal.", ' ', hasAttachmentRef(values.drift_annex)
                                            ? attachmentLabel(attachments, values.drift_annex)
                                            : 'Bilaga ____', "."] })), _jsxs("p", { className: "mt-3 m-0 text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "Justering av prelimin\u00E4rt belopp. " }), "Hyresv\u00E4rden \u00E4ger r\u00E4tt att under l\u00F6pande kalender\u00E5r justera det prelimin\u00E4ra hyrestill\u00E4gget om Hyresv\u00E4rdens faktiska kostnader v\u00E4sentligt avviker fr\u00E5n det prelimin\u00E4ra beloppet. Hyresv\u00E4rden ska skriftligen meddela Hyresg\u00E4sten om s\u00E5dan justering senast 30 dagar innan den tr\u00E4der i kraft."] }), _jsxs("p", { className: "mt-3 m-0 text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "Moms. " }), "P\u00E5 hyrestill\u00E4gg enligt denna paragraf tillkommer merv\u00E4rdesskatt i den m\u00E5n Hyresv\u00E4rden \u00E4r skattskyldig f\u00F6r uthyrningen."] }), _jsxs("p", { className: "mt-3 m-0 text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "font-semibold", children: "Betalning. " }), "Hyrestill\u00E4gg enligt denna paragraf erl\u00E4ggs i f\u00F6rskott senast sista vardagen f\u00F6re varje kalenderm\u00E5nads eller kalenderkvartals b\u00F6rjan, i enlighet med vad som anges i \u00A7 7 (Hyrans betalning)."] })] })] }) }), _jsxs(Section, { sectionRef: "avfallshantering", title: "Avfallshantering", children: [_jsx("p", { className: "m-0 text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten ska tillse att all avfallshantering sker i enlighet med f\u00F6r Fastigheten vid var tid g\u00E4llande best\u00E4mmelser och rekommendationer." }), _jsx("div", { className: "mt-2", children: _jsx(ClauseCheck, { checked: cond(mode, !!values.waste_compactor_allowed), children: "Hyresg\u00E4sten f\u00E5r nyttja Fastighetens komprimatorer." }) }), _jsx("p", { className: "mt-3 mb-1 text-[10.5px] font-semibold text-stone-700", children: "Borttransport av avfall" }), _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(ClauseCheck, { checked: cond(mode, values.waste_transport_mode === 'tenant_own'), children: "Hyresg\u00E4sten svarar f\u00F6r och bekostar borttransport av avfall som h\u00E4rr\u00F6r fr\u00E5n den verksamhet som Hyresg\u00E4sten bedriver i Lokalen. Hyresg\u00E4sten f\u00F6rbinder sig att med renh\u00E5llningsentrepren\u00F6r teckna och vidmakth\u00E5lla kontrakt om borttransport av avfall." }), _jsxs(ClauseCheck, { checked: cond(mode, values.waste_transport_mode === 'landlord_coordinated'), children: ["Hyresv\u00E4rden samordnar borttransport av avfall f\u00F6r flera hyresg\u00E4sters verksamhet i Fastigheten. Hyresg\u00E4sten ska som till\u00E4gg till hyran betala ers\u00E4ttning f\u00F6r Lokalens andel av kostnaden. Lokalens andel ska anses vara", ' ', _jsx("span", { className: "border-b border-stone-400 px-1 font-serif", children: typeof values.waste_share_percent === 'string' && values.waste_share_percent.length > 0
                                            ? values.waste_share_percent
                                            : '____' }), ' ', "%. Hyrestill\u00E4gget uppg\u00E5r vid avtalets tecknande till", ' ', _jsx("span", { className: "border-b border-stone-400 px-1 font-serif", children: typeof values.waste_yearly_amount === 'number'
                                            ? `${num(values.waste_yearly_amount)} kr/år`
                                            : '____ kr/år' }), ".", ' ', hasAttachmentRef(values.waste_annex)
                                        ? attachmentLabel(attachments, values.waste_annex)
                                        : 'Bilaga ____', "."] }), _jsx(ClauseCheck, { checked: cond(mode, values.waste_transport_mode === 'included_in_rent'), children: "Kostnaden f\u00F6r borttransport av avfall ing\u00E5r i hyran." }), _jsxs(ClauseCheck, { checked: cond(mode, values.waste_transport_mode === 'per_annex'), children: ["S\u00E4rskild reglering enligt bilaga.", ' ', hasAttachmentRef(values.waste_annex)
                                        ? attachmentLabel(attachments, values.waste_annex)
                                        : 'Bilaga ____', "."] })] })] }), _jsx(Section, { sectionRef: "bevakning", title: "Bevakning", children: _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(ClauseCheck, { checked: cond(mode, values.bevakning_mode === 'tenant_own'), children: "Hyresv\u00E4rden ombes\u00F6rjer inte bevakning av Fastigheten. Har Hyresg\u00E4sten behov av bevakning av sin verksamhet ska Hyresg\u00E4sten sj\u00E4lv ansvara f\u00F6r och bekosta denna." }), _jsxs(ClauseCheck, { checked: cond(mode, values.bevakning_mode === 'landlord_provides'), children: ["Hyresv\u00E4rden tillhandah\u00E5ller bevakning av Fastigheten. Hyresg\u00E4sten ska som till\u00E4gg till hyran betala ers\u00E4ttning f\u00F6r Lokalens andel av kostnaden. Lokalens andel ska anses vara", ' ', _jsx("span", { className: "border-b border-stone-400 px-1 font-serif", children: typeof values.bevakning_share_percent === 'string' && values.bevakning_share_percent.length > 0
                                        ? values.bevakning_share_percent
                                        : '____' }), ' ', "%. Hyrestill\u00E4gget uppg\u00E5r vid avtalets tecknande till", ' ', _jsx("span", { className: "border-b border-stone-400 px-1 font-serif", children: typeof values.bevakning_yearly_amount === 'number'
                                        ? `${num(values.bevakning_yearly_amount)} kr/år`
                                        : '____ kr/år' }), "."] })] }) }), _jsxs(Section, { sectionRef: "sakerhet", title: "S\u00E4kerhet", children: [_jsx(FieldRow, { label: "S\u00E4kerhetstyp", children: v(values.security_type) }), cond(mode, values.security_type === 'Deposition') && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Antal m\u00E5nadshyror", children: num(values.security_months) }), _jsx(FieldRow, { label: "Belopp (kr)", children: num(values.security_amount) }), _jsx(FieldRow, { label: "Depositionskonto", children: v(values.security_account) })] })), cond(mode, values.security_type === 'Hyresgarantiförsäkring (individuell)' ||
                        values.security_type === 'Hyresgarantiförsäkring (gruppförsäkring)') && (_jsx(FieldRow, { label: "F\u00F6rs\u00E4kringspremie (kr/\u00E5r)", children: num(values.insurance_premium) })), cond(mode, values.security_type === 'Proprieborgen') && (_jsxs(_Fragment, { children: [_jsx("div", { className: "mt-2 mb-1 font-sans text-[10px] uppercase tracking-[0.1em] text-stone-500", children: "Borgensman" }), _jsx(FieldRow, { label: "Namn", children: v(values.guarantor_name) }), _jsx(FieldRow, { label: "Personnr/org.nr", children: v(values.guarantor_org_number) }), _jsx(FieldRow, { label: "Adress", children: v(values.guarantor_address) }), _jsx(FieldRow, { label: "Telefon", children: v(values.guarantor_phone) }), _jsx(FieldRow, { label: "E-post", children: v(values.guarantor_email) }), _jsx(FieldRow, { label: "Borgens\u00E5tagande \u2013 slutdatum", children: v(values.guarantor_end_date) })] }))] }), _jsxs(Section, { sectionRef: "besittningsskydd", title: "Besittningsskydd", children: [_jsx(FieldRow, { label: "Besittningsskydd", children: v(values.possession_protection) }), cond(mode, values.possession_protection ===
                        'Hyresgästen avstår från besittningsskydd – godkänt av hyresnämnden' ||
                        values.possession_protection ===
                            'Hyresgästen avstår från besittningsskydd – ej föremål för prövning') && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Avst\u00E5ende fr.o.m.", children: v(values.possession_waiver_from) }), _jsx(FieldRow, { label: "Avst\u00E5ende t.o.m.", children: v(values.possession_waiver_to) }), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Enligt s\u00E4rskilt uppr\u00E4ttad \u00F6verenskommelse om avst\u00E5ende fr\u00E5n besittningsskydd." })] }))] }), _jsx(Section, { sectionRef: "las-stold", title: "Lokalens l\u00E5s- och st\u00F6ldskyddsanordningar", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: values.lock_responsibility === 'Enligt särskilda villkor i bilaga'
                        ? `Ansvar för anordnandet av lokalens lås- och stöldskyddsanordningar ska följa av särskilda villkor enligt ${attachmentLabel(attachments, values.lock_annex)}.`
                        : values.lock_responsibility === 'Hyresvärden ansvarar'
                            ? 'Hyresvärden ansvarar för att lokalen utrustas med sådana lås- och stöldskyddsanordningar som krävs för hyresgästens affärs- och företagsverksamhet och godkänns av hyresgästens företagsförsäkring.'
                            : 'Hyresgästen ansvarar för att lokalen utrustas med sådana lås- och stöldskyddsanordningar som krävs för hyresgästens affärs- och företagsverksamhet och godkänns av hyresgästens företagsförsäkring.' }) }), _jsx(Section, { sectionRef: "underhall", title: "Underh\u00E5llsansvar", children: _jsxs(SubClauseProvider, { entries: [
                        { id: 'lokalen', visible: true },
                        { id: 'gemensamma', visible: true },
                        { id: 'biutrymmen', visible: ancillaryShown },
                        { id: 'pafoljd', visible: true },
                    ], children: [_jsx(SubClause, { id: "lokalen", title: "Avseende lokalen", children: _jsx(ClauseChoice, { options: MAINTENANCE_PREMISES_OPTIONS, selected: values.maintenance_premises, annexValue: "Annan f\u00F6rdelning enligt bilaga (gr\u00E4nsdragningslista)", annexLabel: hasAttachmentRef(values.maintenance_premises_annex)
                                    ? attachmentLabel(attachments, values.maintenance_premises_annex)
                                    : undefined }) }), _jsx(SubClause, { id: "gemensamma", title: "Avseende allm\u00E4nna och gemensamma utrymmen", children: _jsx(ClauseChoice, { options: MAINTENANCE_COMMON_OPTIONS, selected: values.maintenance_common, annexValue: "Annan \u00F6verenskommelse enligt bilaga", annexLabel: hasAttachmentRef(values.maintenance_common_annex)
                                    ? attachmentLabel(attachments, values.maintenance_common_annex)
                                    : undefined }) }), _jsx(SubClause, { id: "biutrymmen", title: "Avseende tillh\u00F6rande biutrymmen", children: _jsx(ClauseChoice, { options: MAINTENANCE_ANCILLARY_OPTIONS, selected: values.maintenance_ancillary, annexValue: "Annan f\u00F6rdelning enligt bilaga (gr\u00E4nsdragningslista)", annexLabel: hasAttachmentRef(values.maintenance_ancillary_annex)
                                    ? attachmentLabel(attachments, values.maintenance_ancillary_annex)
                                    : undefined }) }), _jsx(SubClause, { id: "pafoljd", title: "P\u00E5f\u00F6ljd vid hyresg\u00E4sts \u00E5sidos\u00E4ttande av underh\u00E5llsskyldighet", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: "Om hyresg\u00E4sten \u00E5sidos\u00E4tter sin underh\u00E5llsskyldighet enligt ovan och inte inom sk\u00E4lig tid vidtar r\u00E4ttelse efter skriftlig uppmaning har hyresv\u00E4rden r\u00E4tt att fullg\u00F6ra skyldigheten p\u00E5 hyresg\u00E4stens bekostnad." }) })] }) }), _jsx(Section, { sectionRef: "ombyggnad", title: "Ombyggnads- och \u00E4ndringsarbeten", children: _jsxs(SubClauseProvider, { entries: [
                        { id: 'ratt', visible: true },
                        { id: 'insyn', visible: true },
                        { id: 'byggvarudeklaration', visible: cond(mode, !!values.byggvarudeklaration_included) },
                        { id: 'avgifter', visible: true },
                        { id: 'nedsattning', visible: true },
                    ], children: [_jsx(SubClause, { id: "ratt", title: "Hyresg\u00E4stens r\u00E4tt att utf\u00F6ra ombyggnads- eller \u00E4ndringsarbeten", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten f\u00E5r inte, utan hyresv\u00E4rdens skriftliga medgivande, utf\u00F6ra ombyggnads- eller \u00E4ndringsarbeten. Vid hyresf\u00F6rh\u00E5llandets upph\u00F6rande ska hyresg\u00E4sten \u00E5terst\u00E4lla lokalen i godtagbart skick." }) }), _jsx(SubClause, { id: "insyn", title: "Hyresv\u00E4rdens insyn under byggtid", children: _jsxs("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: ["Hyresg\u00E4sten ska bereda Hyresv\u00E4rden insyn i ombyggnadsprojekt, bjuda in Hyresv\u00E4rden till byggm\u00F6ten och, n\u00E4r Hyresv\u00E4rden beg\u00E4r det, ge Hyresv\u00E4rden tilltr\u00E4de till Lokalen under ombyggnadstiden.", '\n\n', "Hyresg\u00E4sten svarar f\u00F6r att arbeten i Lokalen inte skadar byggnaden eller st\u00F6r den verksamhet som andra hyresg\u00E4ster i byggnaden bedriver och \u00E4r skyldig att i sk\u00E4lig omfattning ers\u00E4tta Hyresv\u00E4rden om krav p\u00E5 neds\u00E4ttning av hyra riktas mot denne med anledning av hinder eller men i annans nyttjander\u00E4tt.", '\n\n', "Om parterna genomf\u00F6r en ombyggnation eller omdisponering av Lokalen ska nya ritningar uppr\u00E4ttas. Kostnaden f\u00F6r uppr\u00E4ttandet b\u00E4ras av den part som p\u00E5kallat f\u00F6r\u00E4ndringen."] }) }), _jsx(SubClause, { id: "byggvarudeklaration", title: "Byggvarudeklaration", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: "Om Hyresg\u00E4sten utf\u00F6r arbeten avseende Lokalen ska Hyresg\u00E4sten f\u00F6r Hyresv\u00E4rden i god tid f\u00F6re arbetets utf\u00F6rande f\u00F6rete byggvarudeklarationer \u2013 i den m\u00E5n s\u00E5dana finns utarbetade \u2013 f\u00F6r de produkter och material som ska tillf\u00F6ras Lokalen." }) }), _jsx(SubClause, { id: "avgifter", title: "Offentligr\u00E4ttsliga avgifter vid olovliga \u00E4ndringar", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: "Om Hyresg\u00E4sten utan erforderligt bygglov, annat lov eller tillst\u00E5nd vidtar \u00E4ndringar avseende Lokalen och Hyresv\u00E4rden till f\u00F6ljd av detta enligt reglerna i vid var tid g\u00E4llande regler i plan- och bygglagen (PBL) eller annan lag som ers\u00E4tter denna, tvingas utge byggsanktionsavgift eller vite, ska Hyresg\u00E4sten till Hyresv\u00E4rden betala ers\u00E4ttning med motsvarande belopp." }) }), _jsx(SubClause, { id: "nedsattning", title: "Neds\u00E4ttning av hyra vid sedvanligt underh\u00E5ll", children: _jsxs(SubClauseProvider, { format: "letter", entries: [
                                    { id: 'avtalat-skick', visible: true },
                                    { id: 'sedvanligt-underhall', visible: true },
                                ], children: [_jsx(SubClause, { id: "avtalat-skick", title: "Avtalat skick m.m.", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten har inte r\u00E4tt till neds\u00E4ttning av hyran f\u00F6r tid d\u00E5 Hyresv\u00E4rden l\u00E5ter utf\u00F6ra arbete f\u00F6r att s\u00E4tta Lokalen i avtalat skick eller annat arbete som s\u00E4rskilt anges i detta avtal med tillh\u00F6rande bilagor." }) }), _jsx(SubClause, { id: "sedvanligt-underhall", title: "Sedvanligt underh\u00E5ll", children: _jsx(ClauseChoice, { options: SEDVANLIGT_UNDERHALL_OPTIONS, selected: values.sedvanligt_underhall_alt, annexValue: "C", annexLabel: hasAttachmentRef(values.sedvanligt_underhall_annex)
                                                ? attachmentLabel(attachments, values.sedvanligt_underhall_annex)
                                                : undefined }) })] }) })] }) }), _jsxs(Section, { sectionRef: "skyltning", title: "Skyltning", children: [_jsxs("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: ["Hyresg\u00E4sten f\u00E5r inte utan hyresv\u00E4rdens skriftliga medgivande s\u00E4tta upp skylt p\u00E5 egen bekostnad. Hyresg\u00E4sten ansvarar f\u00F6r tillst\u00E5nd fr\u00E5n myndighet och skyltens f\u00F6renlighet med eventuellt f\u00F6rekommande skyltprogram.", '\n\n', "Hyresv\u00E4rden \u00E4ger inte r\u00E4tt att v\u00E4gra samtycke till sedvanlig skylt utan befogad anledning.", '\n\n', "\u00D6vriga anordningar s\u00E5som markiser och antenner f\u00E5r inte s\u00E4ttas upp utan Hyresv\u00E4rdens tillst\u00E5nd.", '\n\n', "Vid mera omfattande fastighetsunderh\u00E5ll s\u00E5som fasadrenovering ska Hyresg\u00E4sten p\u00E5 egen bekostnad och utan ers\u00E4ttning nedmontera och \u00E5ter uppmontera skyltar och andra anordningar som Hyresg\u00E4sten satt upp p\u00E5 byggnaden.", '\n\n', "Hyresv\u00E4rden f\u00F6rbinder sig att inte s\u00E4tta upp automater och skyltsk\u00E5p p\u00E5 ytterv\u00E4ggarna till den av Hyresg\u00E4sten f\u00F6rhyrda Lokalen utan Hyresg\u00E4stens medgivande samt medger Hyresg\u00E4sten f\u00F6retr\u00E4desr\u00E4tt att upps\u00E4tta automater och skyltsk\u00E5p p\u00E5 ifr\u00E5gavarande v\u00E4ggar.", '\n\n', "Vid avflyttning ska Hyresg\u00E4sten \u00E5terst\u00E4lla husfasaden i godtagbart skick."] }), cond(mode, !!values.skyltprogram_included) && (_jsxs("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("span", { className: "mr-1.5", children: "\u2611" }), "Hyresg\u00E4sten f\u00F6rbinder sig att f\u00F6lja bifogat skyltprogram", hasAttachmentRef(values.skyltprogram_annex)
                                ? ` (${attachmentLabel(attachments, values.skyltprogram_annex)})`
                                : '', "."] }))] }), _jsx(Section, { sectionRef: "ledningar", title: "Ledningar f\u00F6r telefoni m.m.", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten bekostar dragning av ledningar f\u00F6r telefoni m.m. fr\u00E5n den anslutningspunkt operat\u00F6ren anger till de st\u00E4llen i lokalen som hyresg\u00E4sten i samr\u00E5d med hyresv\u00E4rden v\u00E4ljer." }) }), _jsx(Section, { sectionRef: "myndighetsforeskrifter", title: "Myndighetsf\u00F6reskrifter", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten ansvarar f\u00F6r och bekostar alla tillst\u00E5nd fr\u00E5n myndigheter som kr\u00E4vs f\u00F6r lokalens anv\u00E4ndning f\u00F6r avsett \u00E4ndam\u00E5l. Vidare ska hyresg\u00E4sten utrusta lokalen med s\u00E5dan utrustning, t.ex. f\u00F6r brandbek\u00E4mpning, som myndighet kan komma att p\u00E5fordra f\u00F6r den verksamhet som bedrivs i lokalen." }) }), _jsxs(Section, { sectionRef: "brandskydd", title: "Brandskydd", children: [_jsx("p", { className: "m-0 text-[10.5px] leading-relaxed text-stone-700", children: "Enligt lagen om skydd mot olyckor (2003:778) ska \u00E4gare eller nyttjander\u00E4ttshavare till byggnader, lokaler eller andra anl\u00E4ggningar vidta de \u00E5tg\u00E4rder som beh\u00F6vs f\u00F6r att f\u00F6rebygga brand och f\u00F6r att hindra eller begr\u00E4nsa skador till f\u00F6ljd av brand. \u00C5tg\u00E4rderna kan vara av teknisk eller organisatorisk karakt\u00E4r." }), _jsxs(SubClauseProvider, { entries: [
                            { id: 'tilltrade', visible: true },
                            { id: 'underhall-utbyte', visible: true },
                            { id: 'sprinkler', visible: true },
                            { id: 'systematiskt', visible: true },
                        ], children: [_jsx(SubClause, { id: "tilltrade", title: "Ansvar vid tilltr\u00E4det", children: _jsxs("div", { className: "flex flex-col gap-1.5", children: [_jsx(ClauseCheck, { checked: cond(mode, values.brandskydd_initial_mode === 'landlord'), children: "Hyresv\u00E4rden ansvarar f\u00F6r att Lokalen vid tilltr\u00E4det uppfyller kraven p\u00E5 brandskydd som kr\u00E4vs f\u00F6r det avtalade anv\u00E4ndnings\u00E4ndam\u00E5let." }), _jsxs(ClauseCheck, { checked: cond(mode, values.brandskydd_initial_mode === 'per_annex'), children: ["Parterna har kommit \u00F6verens om f\u00F6rdelningen av ansvaret f\u00F6r brandskydd inf\u00F6r tilltr\u00E4det. Parternas ansvar regleras i bilaga.", ' ', hasAttachmentRef(values.brandskydd_initial_annex)
                                                    ? attachmentLabel(attachments, values.brandskydd_initial_annex)
                                                    : 'Bilaga ____', "."] })] }) }), _jsx(SubClause, { id: "underhall-utbyte", title: "Underh\u00E5ll och utbyte", children: _jsxs("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: ["Hyresg\u00E4sten ansvarar f\u00F6r underh\u00E5ll och utbyte av s\u00E5dant brandskydd i Lokalen som \u00E4r synligt och \u00E5tkomligt f\u00F6r denne, s\u00E5som n\u00F6dutg\u00E5ngsskyltar och andra brandskyddsanordningar som enbart betj\u00E4nar Lokalen och som inte \u00E4r styrda av byggnadens centrala system. Ansvarsf\u00F6rdelning i \u00F6vrigt framg\u00E5r av gr\u00E4nsdragningslistan.", ' ', hasAttachmentRef(values.brandskydd_maintenance_annex)
                                            ? attachmentLabel(attachments, values.brandskydd_maintenance_annex)
                                            : 'Bilaga ____', ".", '\n\n', "Hyresg\u00E4sten svarar f\u00F6r s\u00E5dana brandskyddsanordningar som Hyresg\u00E4sten har tillf\u00F6rt Lokalen.", '\n\n', "Hyresg\u00E4sten ansvarar f\u00F6r och bekostar d\u00E4rut\u00F6ver s\u00E5dant brandskydd som efter tilltr\u00E4det kan komma att kr\u00E4vas f\u00F6r Lokalens nyttjande f\u00F6r avsedd anv\u00E4ndning.", '\n\n', "Hyresg\u00E4sten ska utan dr\u00F6jsm\u00E5l anm\u00E4la brister som denne uppt\u00E4cker eller borde uppt\u00E4cka i det brandskydd som Hyresv\u00E4rden tillhandah\u00E5ller och svarar f\u00F6r."] }) }), _jsx(SubClause, { id: "sprinkler", title: "Sprinklerarbeten", children: _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed text-stone-700", children: "Eventuella sprinklerarbeten i Lokalen ska utf\u00F6ras av Hyresv\u00E4rden anvisad personal. Kostnaden f\u00F6r s\u00E5dana sprinklerarbeten b\u00E4rs av den part som f\u00F6ranleder \u00E5tg\u00E4rden." }) }), _jsxs(SubClause, { id: "systematiskt", title: "Systematiskt brandskyddsarbete", children: [_jsx("p", { className: "m-0 text-[10.5px] leading-relaxed text-stone-700", children: "Parterna \u00E5tar sig att bedriva ett systematiskt brandskyddsarbete i enlighet med de allm\u00E4nna r\u00E5d som beh\u00F6rig myndighet meddelat. Vardera part \u00E4r dessutom skyldig att en g\u00E5ng om \u00E5ret bereda den andre parten tillf\u00E4lle att f\u00E5 ta del av dokumentation och uppf\u00F6ljning av partens systematiska brandskyddsarbete." }), _jsx("p", { className: "mt-2 mb-1 text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten f\u00F6rbinder sig att se till att:" }), _jsxs("ul", { className: "ml-4 list-disc text-[10.5px] leading-relaxed text-stone-700", children: [_jsx("li", { children: "r\u00E4ddnings- och utrymningsv\u00E4gar alltid h\u00E5lls \u00F6ppna och inte vare sig helt eller delvis blockeras," }), _jsx("li", { children: "funktionen av sprinkler- och andra brandskyddsanordningar aldrig f\u00F6rs\u00E4mras genom t.ex. f\u00F6rbyggnad eller uppst\u00E4llning av f\u00F6rem\u00E5l," }), _jsx("li", { children: "brandd\u00F6rrar h\u00E5lls st\u00E4ngda och brandceller \u00E4r intakta, och" }), _jsx("li", { children: "plomberingar av handtag aldrig bryts annat \u00E4n i h\u00E4ndelse av brand eller brand\u00F6vning." })] })] })] })] }), _jsx(Section, { sectionRef: "utrymningsvagar", title: "Utrymningsv\u00E4gar", children: _jsx("p", { className: "m-0 text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten f\u00F6rbinder sig att inte p\u00E5 n\u00E5got s\u00E4tt blockera i Lokalen bel\u00E4gna utrymningsv\u00E4gar samt till\u00E5ter att Fastighetens \u00F6vriga hyresg\u00E4ster nyttjar dessa vid n\u00F6dsituationer." }) }), _jsx(Section, { sectionRef: "skatter-avgifter", title: "Skatter och avgifter", children: (() => {
                    const cats = Array.isArray(values.unforeseen_cost_categories)
                        ? values.unforeseen_cost_categories
                        : [];
                    const has172 = mode === 'blank' || cats.length > 0;
                    const has172a = mode === 'blank' || cats.includes('ombyggnadsatgarder');
                    const skattOn = cond(mode, cats.includes('skatt_avgift_palaga'));
                    const ombyggOn = cond(mode, cats.includes('ombyggnadsatgarder'));
                    return (_jsxs(SubClauseProvider, { entries: [
                            { id: 'fastighetsskatt', visible: true },
                            { id: 'oforutsedda', visible: has172 },
                            { id: 'moms', visible: true },
                        ], children: [_jsx(SubClause, { id: "fastighetsskatt", title: "Fastighetsskatt", children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(ClauseCheck, { checked: cond(mode, values.property_tax_mode === 'included'), children: "Fastighetsskatt ing\u00E5r i hyran." }), _jsxs(ClauseCheck, { checked: cond(mode, values.property_tax_mode === 'reimbursed_per_annex'), children: ["Fastighetsskatt ers\u00E4tts i enlighet med bifogad fastighetsskatteklausul.", ' ', hasAttachmentRef(values.property_tax_annex)
                                                    ? attachmentLabel(attachments, values.property_tax_annex)
                                                    : 'Bilaga ____'] })] }) }), _jsxs(SubClause, { id: "oforutsedda", title: "Of\u00F6rutsedda kostnader \u2013 andelstal", children: [_jsx("p", { className: "m-0 mt-1 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: "Skulle efter avtalets tecknande of\u00F6rutsedda kostnads\u00F6kningar uppkomma f\u00F6r fastigheten p\u00E5 grund av" }), _jsxs("div", { className: "mt-1 flex flex-col gap-1", children: [_jsx(ClauseCheck, { checked: skattOn, children: "inf\u00F6rande eller h\u00F6jning av skatt, avgift eller p\u00E5laga som riksdag, regering, myndighet eller kommun beslutar om, eller" }), _jsx(ClauseCheck, { checked: ombyggOn, children: "generella ombyggnads\u00E5tg\u00E4rder eller liknande p\u00E5 fastigheten som hyresv\u00E4rden \u00E4r skyldig att utf\u00F6ra till f\u00F6ljd av beslut fr\u00E5n offentligt organ och som till n\u00E5gon del omfattar del av fastigheten," })] }), _jsx("p", { className: "m-0 mt-2 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: "ska hyresg\u00E4sten med verkan fr\u00E5n intr\u00E4dd kostnads\u00F6kning betala ers\u00E4ttning till hyresv\u00E4rden f\u00F6r den p\u00E5 lokalen bel\u00F6pande andelen av den totala \u00E5rliga kostnads\u00F6kningen f\u00F6r fastigheten." }), _jsx(SubClauseProvider, { format: "letter", entries: [{ id: 'andelstal', visible: has172a }], children: _jsx(SubClause, { id: "andelstal", title: "Lokalens andelstal", children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsxs(ClauseCheck, { checked: cond(mode, values.share_calc_mode === 'percent'), children: ["Lokalens andel ska under hyrestiden anses vara", ' ', _jsx("span", { className: "border-b border-stone-400 px-1 font-serif", children: typeof values.share_percent === 'string' &&
                                                                    values.share_percent.length > 0
                                                                    ? values.share_percent
                                                                    : '____' }), ' ', "% av fastighetens totala yta."] }), _jsx(ClauseCheck, { checked: cond(mode, values.share_calc_mode === 'rent_based'), children: "Andelen har inte angetts. Den ber\u00E4knas i st\u00E4llet som hyresg\u00E4stens hyra (exkl. eventuell moms) i f\u00F6rh\u00E5llande till de vid tidpunkten f\u00F6r kostnads\u00F6kningen utg\u00E5ende hyrorna i fastigheten f\u00F6r lokaler och bostadsl\u00E4genheter. F\u00F6r outhyrda enheter g\u00F6rs en uppskattning av marknadshyra respektive bruksv\u00E4rdeshyra." })] }) }) })] }), _jsxs(SubClause, { id: "moms", title: "Merv\u00E4rdesskatt (moms)", children: [_jsxs(SubClauseProvider, { format: "letter", entries: [
                                            { id: 'tenant', visible: true },
                                            { id: 'landlord', visible: true },
                                        ], children: [_jsx(SubClause, { id: "tenant", title: "Hyresg\u00E4stens momsplikt", children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(ClauseCheck, { checked: cond(mode, values.tenant_moms_mode === 'momspliktig'), children: "Hyresg\u00E4sten ska bedriva momspliktig verksamhet i lokalen." }), _jsx(ClauseCheck, { checked: cond(mode, values.tenant_moms_mode === 'ej_momspliktig'), children: "Hyresg\u00E4sten ska inte bedriva momspliktig verksamhet i lokalen." })] }) }), _jsx(SubClause, { id: "landlord", title: "Hyresv\u00E4rdens momsplikt", children: _jsxs("div", { className: "flex flex-col gap-1", children: [_jsx(ClauseCheck, { checked: cond(mode, values.landlord_moms_mode === 'frivillig_aktiv'), children: "Fastighets\u00E4garen/hyresv\u00E4rden till\u00E4mpar frivillig beskattning f\u00F6r moms f\u00F6r uthyrningen av lokalen. Hyresg\u00E4sten ska ut\u00F6ver hyran erl\u00E4gga vid varje tillf\u00E4lle g\u00E4llande moms." }), _jsx(ClauseCheck, { checked: cond(mode, values.landlord_moms_mode === 'frivillig_vid_val'), children: "Om fastighets\u00E4garen/hyresv\u00E4rden v\u00E4ljer frivillig beskattning f\u00F6r moms f\u00F6r uthyrning av lokalen ska hyresg\u00E4sten ut\u00F6ver hyran erl\u00E4gga vid varje tillf\u00E4lle g\u00E4llande moms." })] }) })] }), _jsxs("p", { className: "m-0 mt-3 whitespace-pre-wrap text-[10.5px] leading-relaxed text-stone-700", children: ["Momsen, som ska betalas samtidigt med hyran, ber\u00E4knas p\u00E5 angivet hyresbelopp j\u00E4mte, enligt vid varje tidpunkt g\u00E4llande regler, p\u00E5 f\u00F6rekommande till\u00E4gg och andra ers\u00E4ttningar enligt avtalet.", '\n\n', "Om hyresv\u00E4rden till f\u00F6ljd av hyresg\u00E4stens sj\u00E4lvst\u00E4ndiga agerande \u2013 s\u00E5som uppl\u00E5telse av lokalen helt eller delvis i andra hand, \u00E4ven till eget bolag, eller \u00F6verl\u00E5telse \u2013 blir skyldig att justera avdragen moms enligt merv\u00E4rdesskattelagen, ska hyresg\u00E4sten fullt ut ers\u00E4tta hyresv\u00E4rden f\u00F6r dennes f\u00F6rlorade avdragsr\u00E4tt. Hyresg\u00E4sten ska vidare utge ers\u00E4ttning f\u00F6r den kostnads\u00F6kning som f\u00F6ljer av hyresv\u00E4rdens f\u00F6rlorade avdragsr\u00E4tt f\u00F6r ing\u00E5ende moms p\u00E5 driftskostnader som uppst\u00E5r genom hyresg\u00E4stens agerande."] })] })] }));
                })() }), _jsx(Section, { sectionRef: "overlatelse", title: "\u00D6verl\u00E5telse", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten f\u00E5r inte utan hyresv\u00E4rdens skriftliga medgivande \u00F6verl\u00E5ta hyresr\u00E4tten eller s\u00E4tta annan i sitt st\u00E4lle." }) }), _jsx(Section, { sectionRef: "storningar", title: "St\u00F6rningar och ordning", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten f\u00E5r inte bedriva eller till\u00E5ta verksamhet i lokalen som orsakar ol\u00E4genhet f\u00F6r fastigheten eller dess \u00F6vriga hyresg\u00E4ster. Hyresg\u00E4sten ansvarar f\u00F6r att anst\u00E4llda, bes\u00F6kare och underentrepren\u00F6rer f\u00F6ljer dessa best\u00E4mmelser. Vid \u00E5sidos\u00E4ttande \u00E4ger hyresv\u00E4rden r\u00E4tt att s\u00E4ga upp avtalet i f\u00F6rtid i enlighet med 12 kap. jordabalken." }) }), _jsx(Section, { sectionRef: "tilltrade", title: "Tilltr\u00E4de f\u00F6r hyresv\u00E4rden", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Hyresv\u00E4rden \u00E4ger r\u00E4tt att efter avisering med sk\u00E4lig f\u00F6rvarning, normalt minst 48 timmar i f\u00F6rv\u00E4g, f\u00E5 tilltr\u00E4de till lokalen f\u00F6r besiktning, underh\u00E5llsarbeten eller visning f\u00F6r blivande hyresg\u00E4st. Vid akut skada eller fara \u00E4ger hyresv\u00E4rden omedelbart tilltr\u00E4de utan f\u00F6reg\u00E5ende avisering." }) }), _jsx(Section, { sectionRef: "forsakring", title: "F\u00F6rs\u00E4kring", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Hyresg\u00E4sten \u00E5tar sig att under hela hyrestiden uppr\u00E4tth\u00E5lla en allm\u00E4n ansvarsf\u00F6rs\u00E4kring och, i f\u00F6rekommande fall, en f\u00F6retagsf\u00F6rs\u00E4kring som t\u00E4cker skador i och p\u00E5 lokalen orsakade av hyresg\u00E4sten eller dennes verksamhet. P\u00E5 beg\u00E4ran ska hyresg\u00E4sten visa upp giltigt f\u00F6rs\u00E4kringsbevis f\u00F6r hyresv\u00E4rden. Hyresv\u00E4rden svarar f\u00F6r fastighetsf\u00F6rs\u00E4kring avseende byggnaden." }) }), _jsx(Section, { sectionRef: "konkurs", title: "Konkurs och obest\u00E5nd", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Om hyresg\u00E4sten f\u00F6rs\u00E4tts i konkurs \u00E4ger hyresv\u00E4rden r\u00E4tt att s\u00E4ga upp hyresavtalet med omedelbar verkan, om inte konkursboet inom en m\u00E5nad fr\u00E5n konkursbeslutet meddelar att det intr\u00E4der i avtalet och st\u00E4ller betryggande s\u00E4kerhet f\u00F6r framtida hyresbetalningar. Detsamma g\u00E4ller om hyresg\u00E4sten ans\u00F6ker om f\u00F6retagsrekonstruktion eller vid \u00F6vrig obest\u00E5ndsl\u00F6shet som kan \u00E4ventyra hyresbetalningarna." }) }), _jsx(Section, { sectionRef: "force-majeure", title: "Force majeure", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Hyresv\u00E4rden fritar sig fr\u00E5n skyldighet att fullg\u00F6ra sin del av avtalet och fr\u00E5n skyldighet att betala skadest\u00E5nd om hyresv\u00E4rdens \u00E5taganden inte alls eller till en onormalt h\u00F6g kostnad kan fullg\u00F6ras p\u00E5 grund av krig, upplopp, arbetsinst\u00E4llelse, blockad, eldsv\u00E5da, explosion eller ingrepp av offentlig myndighet som hyresv\u00E4rden inte r\u00E5der \u00F6ver och inte heller kunnat f\u00F6rutse." }) }), _jsx(Section, { sectionRef: "andringar", title: "\u00C4ndringar av avtalet", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "\u00C4ndringar av avtalet ska vara skriftliga f\u00F6r att vara giltiga." }) }), _jsx(Section, { sectionRef: "lagval-tvist", title: "Lagval och tvist", children: _jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: "Avtalet ska tolkas och till\u00E4mpas enligt svensk r\u00E4tt. Tvister med anledning av avtalet ska avg\u00F6ras av allm\u00E4n domstol." }) }), _jsx(Section, { sectionRef: "bilagor", title: "Bilagor", children: _jsx(AttachmentList, { attachments: attachments }) }), _jsx(Section, { sectionRef: "underskrift", title: "Underskrifter", children: _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                            { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                            { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                            ...(hasCotenant
                                ? [{ label: 'Medhyresgäst', role: 'lessee', ordinal: 1 }]
                                : []),
                            ...(hasGuarantor
                                ? [{ label: 'Borgensman', role: 'guarantor', ordinal: 0 }]
                                : []),
                        ], signatories: signatories }) }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map